// İlk önce CSS import
import '../../../context/AuthContext.css';

// Sonra React ve diğer kütüphane importları
import React, { useState, useEffect, useMemo } from 'react';
import AccountSwitcher from '../../../components/AccountSwitcher';
import DateRangeFilter from '../../../components/filters/DateRangeFilter';
import { formatDateYMD } from '../../../utils/dateRangeHelpers';
import Ga4WebOnlyNote from '../../../components/Ga4WebOnlyNote';
import { useAccounts } from '../../../context/AccountContext';
import { Card } from '../../../components/ui/Card';
import { 
  Filter,
  Download,
  RefreshCw,
  Search,
  ChevronDown,
  ChevronRight,
  LayoutList,
  TrendingUp,
  DollarSign,
  MousePointerClick,
  ShoppingCart,
  Calendar,
  X,
  TrendingDown,
  BarChart3
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Sample ad groups data structure
const sampleAdGroups = {
  'search-1': [
    {
      id: 'ag1',
      name: 'Brand Keywords',
      status: 'Active',
      spend: 5200,
      impressions: 18000,
      clicks: 1200,
      ctr: 6.67,
      cpc: 4.33,
      conversions: 85,
      conversionRate: 7.08,
      costPerConversion: 61.18,
      revenue: 22100,
      roas: 4.25,
      keywords: [
        { id: 'kw1-1', text: 'glowytics brand', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 4200, clicks: 320, ctr: 7.62, cpc: 4.10, conversions: 22, conversionRate: 6.88, costPerConversion: 59.64, revenue: 5800, roas: 4.42 },
        { id: 'kw1-2', text: 'glowytics analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3800, clicks: 280, ctr: 7.37, cpc: 4.25, conversions: 19, conversionRate: 6.79, costPerConversion: 62.63, revenue: 4900, roas: 4.12 },
        { id: 'kw1-3', text: 'glowytics platform', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 4.40, conversions: 16, conversionRate: 6.96, costPerConversion: 63.25, revenue: 4200, roas: 4.15 },
        { id: 'kw1-4', text: 'glowytics software', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3300, clicks: 210, ctr: 6.36, cpc: 4.50, conversions: 15, conversionRate: 7.14, costPerConversion: 63.00, revenue: 3800, roas: 4.02 },
        { id: 'kw1-5', text: 'glowytics marketing', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3200, clicks: 160, ctr: 5.00, cpc: 4.60, conversions: 13, conversionRate: 8.13, costPerConversion: 56.62, revenue: 3400, roas: 4.60 }
      ]
    },
    {
      id: 'ag2',
      name: 'Generic Keywords',
      status: 'Active',
      spend: 4800,
      impressions: 16500,
      clicks: 1100,
      ctr: 6.67,
      cpc: 4.36,
      conversions: 65,
      conversionRate: 5.91,
      costPerConversion: 73.85,
      revenue: 18200,
      roas: 3.79,
      keywords: [
        { id: 'kw2-1', text: 'marketing analytics', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3800, clicks: 250, ctr: 6.58, cpc: 4.20, conversions: 14, conversionRate: 5.60, costPerConversion: 75.00, revenue: 3900, roas: 3.71 },
        { id: 'kw2-2', text: 'analytics platform', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 4.30, conversions: 13, conversionRate: 5.65, costPerConversion: 76.15, revenue: 3700, roas: 3.75 },
        { id: 'kw2-3', text: 'marketing dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3200, clicks: 220, ctr: 6.88, cpc: 4.40, conversions: 13, conversionRate: 5.91, costPerConversion: 74.46, revenue: 3600, roas: 3.72 },
        { id: 'kw2-4', text: 'ads performance tracking', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3100, clicks: 210, ctr: 6.77, cpc: 4.50, conversions: 13, conversionRate: 6.19, costPerConversion: 72.69, revenue: 3500, roas: 3.70 },
        { id: 'kw2-5', text: 'marketing roi tool', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2900, clicks: 190, ctr: 6.55, cpc: 4.60, conversions: 12, conversionRate: 6.32, costPerConversion: 72.83, revenue: 3500, roas: 4.01 }
      ]
    },
    {
      id: 'ag3',
      name: 'Competitor Keywords',
      status: 'Active',
      spend: 2500,
      impressions: 10500,
      clicks: 900,
      ctr: 8.57,
      cpc: 2.78,
      conversions: 30,
      conversionRate: 3.33,
      costPerConversion: 83.33,
      revenue: 12200,
      roas: 4.88,
      keywords: [
        { id: 'kw3-1', text: 'google analytics alternative', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2400, clicks: 200, ctr: 8.33, cpc: 2.70, conversions: 7, conversionRate: 3.50, costPerConversion: 77.14, revenue: 2600, roas: 4.85 },
        { id: 'kw3-2', text: 'better than semrush', status: 'Active', matchType: 'Exact', qualityScore: 6, impressions: 2200, clicks: 190, ctr: 8.64, cpc: 2.75, conversions: 6, conversionRate: 3.16, costPerConversion: 87.08, revenue: 2500, roas: 4.79 },
        { id: 'kw3-3', text: 'hubspot vs', status: 'Active', matchType: 'Broad', qualityScore: 5, impressions: 2100, clicks: 180, ctr: 8.57, cpc: 2.80, conversions: 6, conversionRate: 3.33, costPerConversion: 84.00, revenue: 2400, roas: 4.76 },
        { id: 'kw3-4', text: 'mixpanel alternative', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2000, clicks: 170, ctr: 8.50, cpc: 2.85, conversions: 6, conversionRate: 3.53, costPerConversion: 80.75, revenue: 2400, roas: 4.96 },
        { id: 'kw3-5', text: 'amplitude vs', status: 'Active', matchType: 'Broad', qualityScore: 5, impressions: 1800, clicks: 160, ctr: 8.89, cpc: 2.80, conversions: 5, conversionRate: 3.13, costPerConversion: 89.60, revenue: 2300, roas: 5.11 }
      ]
    }
  ],
  'search-2': [
    {
      id: 'ag4',
      name: 'Product Keywords',
      status: 'Active',
      spend: 3800,
      impressions: 14200,
      clicks: 980,
      ctr: 6.90,
      cpc: 3.88,
      conversions: 52,
      conversionRate: 5.31,
      costPerConversion: 73.08,
      revenue: 15600,
      roas: 4.11,
      keywords: [
        { id: 'kw4-1', text: 'analytics dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3200, clicks: 220, ctr: 6.88, cpc: 3.80, conversions: 12, conversionRate: 5.45, costPerConversion: 69.67, revenue: 3500, roas: 4.18 },
        { id: 'kw4-2', text: 'marketing analytics tool', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 3000, clicks: 210, ctr: 7.00, cpc: 3.85, conversions: 11, conversionRate: 5.24, costPerConversion: 73.55, revenue: 3300, roas: 4.08 },
        { id: 'kw4-3', text: 'ad performance tracker', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2800, clicks: 190, ctr: 6.79, cpc: 3.90, conversions: 10, conversionRate: 5.26, costPerConversion: 74.10, revenue: 3000, roas: 4.05 },
        { id: 'kw4-4', text: 'campaign analytics software', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2700, clicks: 180, ctr: 6.67, cpc: 3.95, conversions: 10, conversionRate: 5.56, costPerConversion: 71.10, revenue: 2900, roas: 4.08 },
        { id: 'kw4-5', text: 'marketing performance dashboard', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 180, ctr: 7.20, cpc: 3.90, conversions: 9, conversionRate: 5.00, costPerConversion: 78.00, revenue: 2900, roas: 4.14 }
      ]
    },
    {
      id: 'ag5',
      name: 'Category Keywords',
      status: 'Active',
      spend: 4200,
      impressions: 15800,
      clicks: 1050,
      ctr: 6.65,
      cpc: 4.00,
      conversions: 58,
      conversionRate: 5.52,
      costPerConversion: 72.41,
      revenue: 17400,
      roas: 4.14,
      keywords: [
        { id: 'kw5-1', text: 'marketing analytics', status: 'Active', matchType: 'Broad', qualityScore: 7, impressions: 3500, clicks: 230, ctr: 6.57, cpc: 3.95, conversions: 13, conversionRate: 5.65, costPerConversion: 69.92, revenue: 3800, roas: 4.18 },
        { id: 'kw5-2', text: 'digital marketing tools', status: 'Active', matchType: 'Phrase', qualityScore: 8, impressions: 3300, clicks: 220, ctr: 6.67, cpc: 4.00, conversions: 12, conversionRate: 5.45, costPerConversion: 73.33, revenue: 3600, roas: 4.09 },
        { id: 'kw5-3', text: 'ad campaign management', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 3100, clicks: 210, ctr: 6.77, cpc: 4.05, conversions: 12, conversionRate: 5.71, costPerConversion: 70.88, revenue: 3500, roas: 4.12 },
        { id: 'kw5-4', text: 'marketing performance tracking', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 3000, clicks: 200, ctr: 6.67, cpc: 4.10, conversions: 11, conversionRate: 5.50, costPerConversion: 74.55, revenue: 3300, roas: 4.02 },
        { id: 'kw5-5', text: 'advertising analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2900, clicks: 190, ctr: 6.55, cpc: 4.15, conversions: 10, conversionRate: 5.26, costPerConversion: 78.85, revenue: 3200, roas: 4.06 }
      ]
    }
  ],
  'search-3': [
    {
      id: 'ag6',
      name: 'Location Keywords',
      status: 'Active',
      spend: 2800,
      impressions: 11200,
      clicks: 780,
      ctr: 6.96,
      cpc: 3.59,
      conversions: 42,
      conversionRate: 5.38,
      costPerConversion: 66.67,
      revenue: 12600,
      roas: 4.50,
      keywords: [
        { id: 'kw6-1', text: 'marketing analytics istanbul', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 170, ctr: 6.80, cpc: 3.55, conversions: 9, conversionRate: 5.29, costPerConversion: 67.06, revenue: 2700, roas: 4.47 },
        { id: 'kw6-2', text: 'ankara digital marketing tools', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2300, clicks: 160, ctr: 6.96, cpc: 3.60, conversions: 9, conversionRate: 5.63, costPerConversion: 64.00, revenue: 2600, roas: 4.51 },
        { id: 'kw6-3', text: 'izmir ad campaign management', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2200, clicks: 150, ctr: 6.82, cpc: 3.65, conversions: 8, conversionRate: 5.33, costPerConversion: 68.44, revenue: 2500, roas: 4.56 },
        { id: 'kw6-4', text: 'antalya marketing analytics', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2100, clicks: 150, ctr: 7.14, cpc: 3.55, conversions: 8, conversionRate: 5.33, costPerConversion: 66.56, revenue: 2400, roas: 4.52 },
        { id: 'kw6-5', text: 'bursa advertising analytics', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2100, clicks: 150, ctr: 7.14, cpc: 3.60, conversions: 8, conversionRate: 5.33, costPerConversion: 67.50, revenue: 2400, roas: 4.44 }
      ]
    }
  ],
  'search-4': [
    {
      id: 'ag7',
      name: 'Seasonal Keywords',
      status: 'Paused',
      spend: 1500,
      impressions: 8200,
      clicks: 420,
      ctr: 5.12,
      cpc: 3.57,
      conversions: 18,
      conversionRate: 4.29,
      costPerConversion: 83.33,
      revenue: 5400,
      roas: 3.60,
      keywords: [
        { id: 'kw7-1', text: 'summer marketing analytics', status: 'Paused', matchType: 'Broad', qualityScore: 6, impressions: 1800, clicks: 90, ctr: 5.00, cpc: 3.55, conversions: 4, conversionRate: 4.44, costPerConversion: 79.88, revenue: 1200, roas: 3.75 },
        { id: 'kw7-2', text: 'holiday season analytics', status: 'Paused', matchType: 'Phrase', qualityScore: 7, impressions: 1700, clicks: 85, ctr: 5.00, cpc: 3.60, conversions: 4, conversionRate: 4.71, costPerConversion: 76.50, revenue: 1100, roas: 3.60 },
        { id: 'kw7-3', text: 'black friday marketing tools', status: 'Paused', matchType: 'Exact', qualityScore: 8, impressions: 1600, clicks: 85, ctr: 5.31, cpc: 3.50, conversions: 4, conversionRate: 4.71, costPerConversion: 74.38, revenue: 1100, roas: 3.69 },
        { id: 'kw7-4', text: 'winter campaign analytics', status: 'Paused', matchType: 'Broad', qualityScore: 6, impressions: 1600, clicks: 80, ctr: 5.00, cpc: 3.60, conversions: 3, conversionRate: 3.75, costPerConversion: 96.00, revenue: 1000, roas: 3.47 },
        { id: 'kw7-5', text: 'seasonal marketing performance', status: 'Paused', matchType: 'Phrase', qualityScore: 7, impressions: 1500, clicks: 80, ctr: 5.33, cpc: 3.55, conversions: 3, conversionRate: 3.75, costPerConversion: 94.67, revenue: 1000, roas: 3.52 }
      ]
    }
  ],
  'search-5': [
    {
      id: 'ag8',
      name: 'Promotion Keywords',
      status: 'Active',
      spend: 3200,
      impressions: 12800,
      clicks: 890,
      ctr: 6.95,
      cpc: 3.60,
      conversions: 48,
      conversionRate: 5.39,
      costPerConversion: 66.67,
      revenue: 14400,
      roas: 4.50,
      keywords: [
        { id: 'kw8-1', text: 'marketing analytics discount', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2800, clicks: 190, ctr: 6.79, cpc: 3.55, conversions: 10, conversionRate: 5.26, costPerConversion: 67.45, revenue: 3100, roas: 4.59 },
        { id: 'kw8-2', text: 'analytics software promotion', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2600, clicks: 180, ctr: 6.92, cpc: 3.60, conversions: 10, conversionRate: 5.56, costPerConversion: 64.80, revenue: 3000, roas: 4.63 },
        { id: 'kw8-3', text: 'special offer marketing tools', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2500, clicks: 175, ctr: 7.00, cpc: 3.65, conversions: 10, conversionRate: 5.71, costPerConversion: 63.88, revenue: 2900, roas: 4.54 },
        { id: 'kw8-4', text: 'marketing dashboard deal', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2500, clicks: 175, ctr: 7.00, cpc: 3.55, conversions: 9, conversionRate: 5.14, costPerConversion: 69.06, revenue: 2800, roas: 4.51 },
        { id: 'kw8-5', text: 'analytics platform offer', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2400, clicks: 170, ctr: 7.08, cpc: 3.60, conversions: 9, conversionRate: 5.29, costPerConversion: 68.00, revenue: 2600, roas: 4.25 }
      ]
    }
  ],
  'search-6': [
    {
      id: 'ag9',
      name: 'Long-tail Keywords',
      status: 'Active',
      spend: 2900,
      impressions: 11600,
      clicks: 810,
      ctr: 6.98,
      cpc: 3.58,
      conversions: 44,
      conversionRate: 5.43,
      costPerConversion: 65.91,
      revenue: 13200,
      roas: 4.55,
      keywords: [
        { id: 'kw9-1', text: 'best marketing analytics platform for small business', status: 'Active', matchType: 'Broad', qualityScore: 7, impressions: 2500, clicks: 170, ctr: 6.80, cpc: 3.55, conversions: 9, conversionRate: 5.29, costPerConversion: 67.06, revenue: 2800, roas: 4.64 },
        { id: 'kw9-2', text: 'how to track marketing campaign performance effectively', status: 'Active', matchType: 'Phrase', qualityScore: 6, impressions: 2400, clicks: 165, ctr: 6.88, cpc: 3.60, conversions: 9, conversionRate: 5.45, costPerConversion: 66.00, revenue: 2700, roas: 4.55 },
        { id: 'kw9-3', text: 'affordable marketing analytics software for startups', status: 'Active', matchType: 'Exact', qualityScore: 8, impressions: 2300, clicks: 160, ctr: 6.96, cpc: 3.55, conversions: 9, conversionRate: 5.63, costPerConversion: 63.11, revenue: 2600, roas: 4.59 },
        { id: 'kw9-4', text: 'marketing roi tracking tool for multiple platforms', status: 'Active', matchType: 'Broad', qualityScore: 6, impressions: 2200, clicks: 160, ctr: 7.27, cpc: 3.60, conversions: 9, conversionRate: 5.63, costPerConversion: 64.00, revenue: 2600, roas: 4.51 },
        { id: 'kw9-5', text: 'all in one marketing analytics dashboard with reporting', status: 'Active', matchType: 'Phrase', qualityScore: 7, impressions: 2200, clicks: 155, ctr: 7.05, cpc: 3.55, conversions: 8, conversionRate: 5.16, costPerConversion: 68.78, revenue: 2500, roas: 4.54 }
      ]
    }
  ],
  'search-7': [
    {
      id: 'ag10',
      name: 'Dynamic Keywords',
      status: 'Active',
      spend: 3500,
      impressions: 13500,
      clicks: 940,
      ctr: 6.96,
      cpc: 3.72,
      conversions: 51,
      conversionRate: 5.43,
      costPerConversion: 68.63,
      revenue: 15300,
      roas: 4.37,
      keywords: [
        { id: 'kw10-1', text: '{marketing analytics} software', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2900, clicks: 200, ctr: 6.90, cpc: 3.70, conversions: 11, conversionRate: 5.50, costPerConversion: 67.27, revenue: 3300, roas: 4.46 },
        { id: 'kw10-2', text: '{digital marketing} dashboard', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2800, clicks: 195, ctr: 6.96, cpc: 3.75, conversions: 11, conversionRate: 5.64, costPerConversion: 66.48, revenue: 3200, roas: 4.38 },
        { id: 'kw10-3', text: '{campaign performance} tracking', status: 'Active', matchType: 'Dynamic', qualityScore: 6, impressions: 2700, clicks: 185, ctr: 6.85, cpc: 3.70, conversions: 10, conversionRate: 5.41, costPerConversion: 68.45, revenue: 3000, roas: 4.38 },
        { id: 'kw10-4', text: '{advertising analytics} platform', status: 'Active', matchType: 'Dynamic', qualityScore: 7, impressions: 2600, clicks: 180, ctr: 6.92, cpc: 3.75, conversions: 10, conversionRate: 5.56, costPerConversion: 67.50, revenue: 2900, roas: 4.30 },
        { id: 'kw10-5', text: '{marketing roi} calculator', status: 'Active', matchType: 'Dynamic', qualityScore: 8, impressions: 2500, clicks: 180, ctr: 7.20, cpc: 3.70, conversions: 9, conversionRate: 5.00, costPerConversion: 74.00, revenue: 2900, roas: 4.35 }
      ]
    }
  ]
};

// Sample chart data
const chartData = {
  labels: ['1 Jan', '2 Jan', '3 Jan', '4 Jan', '5 Jan', '6 Jan', '7 Jan'],
  datasets: [
    {
      label: 'Spend',
      data: [300, 450, 320, 500, 350, 200, 400],
      borderColor: 'rgb(59, 130, 246)',
      backgroundColor: 'rgba(59, 130, 246, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    },
    {
      label: 'Revenue',
      data: [500, 300, 400, 600, 450, 350, 550],
      borderColor: 'rgb(16, 185, 129)',
      backgroundColor: 'rgba(16, 185, 129, 0.1)',
      fill: true,
      tension: 0.4,
      yAxisID: 'y',
    }
  ]
};

const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  scales: {
    y: {
      beginAtZero: true,
      grid: {
        color: 'rgba(0, 0, 0, 0.05)',
      },
      ticks: {
        callback: (value) => `₺${value}`
      }
    },
    x: {
      grid: {
        display: false
      }
    }
  },
  plugins: {
    legend: {
      position: 'top',
      align: 'end',
      labels: {
        boxWidth: 10,
        usePointStyle: true,
        pointStyle: 'circle'
      }
    },
    tooltip: {
      callbacks: {
        label: function(context) {
          let label = context.dataset.label || '';
          if (label) {
            label += ': ';
          }
          if (context.parsed.y !== null) {
            label += `₺${context.parsed.y.toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
          }
          return label;
        }
      }
    }
  },
  interaction: {
    intersect: false,
    mode: 'index',
  }
};

// First, add a couple of sample data arrays for our new tables
// Add this after your other sample data

// Sample asset performance data
const sampleAssetPerformance = [
  { id: 'a1', type: 'Image', title: 'Product Showcase', status: 'Good', impressions: 12500, clicks: 620, ctr: 4.96, conversions: 35, conversionRate: 5.65 },
  { id: 'a2', type: 'Text', title: 'Main Headline', status: 'Best', impressions: 18300, clicks: 980, ctr: 5.36, conversions: 58, conversionRate: 5.92 },
  { id: 'a3', type: 'Video', title: 'Brand Video 30s', status: 'Low', impressions: 8200, clicks: 310, ctr: 3.78, conversions: 14, conversionRate: 4.52 },
  { id: 'a4', type: 'Image', title: 'Lifestyle Photo', status: 'Good', impressions: 11800, clicks: 580, ctr: 4.92, conversions: 32, conversionRate: 5.52 },
  { id: 'a5', type: 'Text', title: 'Description Line 1', status: 'Best', impressions: 17500, clicks: 940, ctr: 5.37, conversions: 55, conversionRate: 5.85 },
  { id: 'a6', type: 'Text', title: 'Description Line 2', status: 'Good', impressions: 16200, clicks: 820, ctr: 5.06, conversions: 48, conversionRate: 5.85 },
  { id: 'a7', type: 'Image', title: 'Feature Image', status: 'Low', impressions: 7200, clicks: 280, ctr: 3.89, conversions: 12, conversionRate: 4.29 },
];

// Sample audience insights data
const sampleAudienceInsights = [
  { id: 's1', segment: 'In-market / Software Services', impressions: 15200, clicks: 820, ctr: 5.39, conversions: 48, conversionRate: 5.85, conversionValue: 12500, roas: 4.2 },
  { id: 's2', segment: 'Affinity / Technology Enthusiasts', impressions: 12800, clicks: 680, ctr: 5.31, conversions: 39, conversionRate: 5.74, conversionValue: 9800, roas: 3.8 },
  { id: 's3', segment: 'Demographics / 25-34', impressions: 18500, clicks: 960, ctr: 5.19, conversions: 56, conversionRate: 5.83, conversionValue: 14200, roas: 4.1 },
  { id: 's4', segment: 'Demographics / Male', impressions: 16800, clicks: 870, ctr: 5.18, conversions: 51, conversionRate: 5.86, conversionValue: 13100, roas: 4.0 },
  { id: 's5', segment: 'Custom Intent / Business Analytics', impressions: 9800, clicks: 530, ctr: 5.41, conversions: 32, conversionRate: 6.04, conversionValue: 8600, roas: 4.5 },
];

export default function PerformanceMax() {
  const { getSelectedAccountId } = useAccounts();
  const selectedAccountId = getSelectedAccountId('GOOGLE_ADS');

  const [isLoading, setIsLoading] = useState(false);
  const [dateRange, setDateRange] = useState('last30');
  const [gaAccountFilter, setGaAccountFilter] = useState('all');
  const [gaAccounts, setGaAccounts] = useState([]);
  const [keywordData, setKeywordData] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaigns, setExpandedCampaigns] = useState(new Set());
  const [expandedAdGroups, setExpandedAdGroups] = useState(new Set());
  // Sıralama için state değişkenleri
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  // Chart verisi için state
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: []
  });

  const [chartOptions, setChartOptions] = useState({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        align: 'end'
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    }
  });

  // Add missing state variables
  const [trendData, setTrendData] = useState([]);
  const [isLoadingTrend, setIsLoadingTrend] = useState(false);

  // Update the state to include the new filter option
  const [selectedDateRange, setSelectedDateRange] = useState('7days');

  // Custom date picker state — must be declared before any hook that references
  // customStartDate/customEndDate (there's a useEffect at the top of the component
  // that depends on them), otherwise hoisting/TDZ throws during render.
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [isDateDropdownOpen, setIsDateDropdownOpen] = useState(false);

  // State kısmına dataLoading ekleyelim
  const [dataLoading, setDataLoading] = useState(true);

  // Add these state variables with the others
  const [assetPerformance, setAssetPerformance] = useState(sampleAssetPerformance);
  const [audienceInsights, setAudienceInsights] = useState(sampleAudienceInsights);

  // State kısmında performanceMaxData için yeni bir state ekleyelim
  const [performanceMaxData, setPerformanceMaxData] = useState({ campaigns: [] });
  const [productGroupsRaw, setProductGroupsRaw] = useState([]);
  const [categorySummary, setCategorySummary] = useState([]);
  const [brandSummary, setBrandSummary] = useState([]);
  const [campaignShoppingSummary, setCampaignShoppingSummary] = useState([]);

  // Segments state — daily/monthly breakdown rows under each campaign
  const [selectedSegment, setSelectedSegment] = useState('none');
  const [isSegmentDropdownOpen, setIsSegmentDropdownOpen] = useState(false);
  const [dailyDataMap, setDailyDataMap] = useState(new Map());

  // Kampanya genişletme/daraltma işlevi
  const toggleCampaign = (campaignId, e) => {
    e.stopPropagation();
    setExpandedCampaigns(prev => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Reklam grubu genişletme/daraltma işlevi
  const toggleAdGroup = (adGroupId, e) => {
    e.stopPropagation();
    setExpandedAdGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(adGroupId)) {
        newSet.delete(adGroupId);
      } else {
        newSet.add(adGroupId);
      }
      return newSet;
    });
  };

  // Sıralama işlevi
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alan üzerine tıklandığında sıralama yönünü değiştir
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Yeni bir alan seçildiğinde, varsayılan olarak azalan sıralama yap
      setSortField(field);
      setSortDirection(field === 'budget' ? 'desc' : 'asc');
    }
  };

  // Anahtar kelimeleri sırala
  const sortKeywords = (keywords, field, direction) => {
    if (!field) return keywords;

    return [...keywords].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;
        
        return direction === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
      
      // Metin değerleri için sıralama (anahtar kelime metni, durum vb.)
      if (['text', 'status'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };

  // Function to organize keyword data into campaigns and ad groups
  const organizeKeywordData = (data) => {
    if (!data || data.length === 0) {
      console.log('No keyword data available');
      return { campaigns: [] };
    }

    console.log(`Organizing ${data.length} keyword records`);

    // Kampanya ve reklam grupları için map'ler oluştur
    const campaignMap = new Map();
    const adGroupMap = new Map();
    
    // Anahtar kelimeleri birleştirmek için bir map oluştur
    const keywordMap = new Map();

    // Önce tüm kampanya ve reklam gruplarını oluştur
    data.forEach(item => {
      const campaignId = item.campaign_id;
      const adGroupId = item.ad_group_id;
      const keywordId = item.keyword_id;
      const keywordText = item.keyword_text?.toLowerCase() || '';

      // Kampanyayı ekle
      if (!campaignMap.has(campaignId)) {
        campaignMap.set(campaignId, {
          id: campaignId,
          name: item.campaign_name || 'Unknown Campaign',
          status: item.campaign_status || 'Unknown',
          budget: parseFloat(item.campaign_budget || 0),
          spend: 0, // Reklam gruplarından hesaplanacak
          impressions: 0, // Reklam gruplarından hesaplanacak
          clicks: 0, // Reklam gruplarından hesaplanacak
          conversions: 0, // Reklam gruplarından hesaplanacak
          conversionValue: 0, // Reklam gruplarından hesaplanacak
          adGroups: [],
          expanded: true
        });
      }

      // Reklam grubunu ekle
      if (!adGroupMap.has(adGroupId)) {
        adGroupMap.set(adGroupId, {
          id: adGroupId,
          name: item.ad_group_name || 'Unknown Ad Group',
          status: item.ad_group_status || 'Unknown',
          campaignId: campaignId,
          spend: 0, // Anahtar kelimelerden hesaplanacak
          impressions: 0, // Anahtar kelimelerden hesaplanacak
          clicks: 0, // Anahtar kelimelerden hesaplanacak
          conversions: 0, // Anahtar kelimelerden hesaplanacak
          conversionValue: 0, // Anahtar kelimelerden hesaplanacak
          keywords: [],
          expanded: false
        });
      }

      // Anahtar kelime için benzersiz bir kimlik oluştur (reklam grubu + anahtar kelime metni)
      const uniqueKeywordId = `${adGroupId}_${keywordText}`;
      
      // Anahtar kelimeyi ekle veya güncelle
      if (!keywordMap.has(uniqueKeywordId)) {
        keywordMap.set(uniqueKeywordId, {
          id: keywordId,
          text: keywordText,
          status: item.keyword_status || 'Unknown',
          adGroupId: adGroupId,
          matchType: item.keyword_match_type || 'Unknown',
          spend: parseFloat(item.cost || 0),
          impressions: parseInt(item.impressions || 0, 10),
          clicks: parseInt(item.clicks || 0, 10),
          conversions: parseInt(item.conversions || 0, 10),
          conversionValue: parseFloat(item.conversion_value || 0),
          matchTypes: [item.keyword_match_type || 'Unknown']
        });
      } else {
        // Mevcut anahtar kelimeyi güncelle (metrikleri topla)
        const existingKeyword = keywordMap.get(uniqueKeywordId);
        existingKeyword.spend += parseFloat(item.cost || 0);
        existingKeyword.impressions += parseInt(item.impressions || 0, 10);
        existingKeyword.clicks += parseInt(item.clicks || 0, 10);
        existingKeyword.conversions += parseInt(item.conversions || 0, 10);
        existingKeyword.conversionValue += parseFloat(item.conversion_value || 0);
        
        // Eşleşme türünü ekle (eğer zaten yoksa)
        if (item.keyword_match_type && !existingKeyword.matchTypes.includes(item.keyword_match_type)) {
          existingKeyword.matchTypes.push(item.keyword_match_type);
        }
      }
    });

    // Anahtar kelimeleri reklam gruplarına ekle ve metrikleri hesapla
    adGroupMap.forEach(adGroup => {
      // Anahtar kelimeleri reklam gruplarına ekle
      const adGroupKeywords = Array.from(keywordMap.values())
        .filter(keyword => keyword.adGroupId === adGroup.id);
      
      adGroup.keywords = adGroupKeywords;
      
      // Anahtar kelimelerden reklam grubu metriklerini hesapla
      adGroupKeywords.forEach(keyword => {
        adGroup.spend += keyword.spend || 0;
        adGroup.impressions += keyword.impressions || 0;
        adGroup.clicks += keyword.clicks || 0;
        adGroup.conversions += keyword.conversions || 0;
        adGroup.conversionValue += keyword.conversionValue || 0;
      });
      
      // CTR, CPC, dönüşüm oranı vb. hesapla
      adGroup.ctr = adGroup.impressions > 0 ? (adGroup.clicks / adGroup.impressions) * 100 : 0;
      adGroup.cpc = adGroup.clicks > 0 ? adGroup.spend / adGroup.clicks : 0;
      adGroup.conversionRate = adGroup.clicks > 0 ? (adGroup.conversions / adGroup.clicks) * 100 : 0;
      adGroup.costPerConversion = adGroup.conversions > 0 ? adGroup.spend / adGroup.conversions : 0;
      adGroup.roas = adGroup.spend > 0 ? adGroup.conversionValue / adGroup.spend : 0;
      
      // Reklam grubunu ilgili kampanyaya ekle
      const campaign = campaignMap.get(adGroup.campaignId);
      if (campaign) {
        campaign.adGroups.push(adGroup);
        
        // Reklam grubu metriklerini kampanya metriklerine ekle
        campaign.spend += adGroup.spend || 0;
        campaign.impressions += adGroup.impressions || 0;
        campaign.clicks += adGroup.clicks || 0;
        campaign.conversions += adGroup.conversions || 0;
        campaign.conversionValue += adGroup.conversionValue || 0;
      }
    });

    // Kampanya metriklerini hesapla
    campaignMap.forEach(campaign => {
      // CTR, CPC, dönüşüm oranı vb. hesapla
      campaign.ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions) * 100 : 0;
      campaign.cpc = campaign.clicks > 0 ? campaign.spend / campaign.clicks : 0;
      campaign.conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks) * 100 : 0;
      campaign.costPerConversion = campaign.conversions > 0 ? campaign.spend / campaign.conversions : 0;
      campaign.roas = campaign.spend > 0 ? campaign.conversionValue / campaign.spend : 0;
      
      // Kampanya gelirini conversionValue olarak ayarla
      campaign.revenue = campaign.conversionValue;
    });

    // Kampanyaları bir diziye dönüştür
    const campaigns = Array.from(campaignMap.values());
    
    console.log(`Organized ${campaigns.length} campaigns with ${adGroupMap.size} ad groups and ${keywordMap.size} unique keywords`);
    
    return { campaigns };
  };

  // Calculate summary metrics from campaigns
  const calculateSummaryMetrics = (campaigns) => {
    if (!campaigns || campaigns.length === 0) {
      return {
        cpc: 0,
        conversionRate: 0,
        cost: 0,
        revenue: 0,
        roas: 0,
        clicks: 0,
        impressions: 0,
        ctr: 0,
        conversions: 0,
        ga4Revenue: 0,
        ga4Roas: 0,
        ga4Conversions: 0,
        ga4ConversionRate: 0,
        sessions: 0,
        adjustSessions: 0,
        cpcChange: 0,
        conversionRateChange: 0,
        costChange: 0,
        revenueChange: 0,
        roasChange: 0
      };
    }

    // Şu anki dönem için metrikleri hesaplama
    let totalClicks = 0;
    let totalCost = 0;
    let totalConversions = 0;
    let totalRevenue = 0;
    let totalImpressions = 0;
    let totalGa4Revenue = 0;
    let totalGa4Conversions = 0;
    let totalSessions = 0;
    let totalAdjustSessions = 0;

    campaigns.forEach(campaign => {
      totalClicks += campaign.clicks || 0;
      totalCost += campaign.spend || 0;
      totalConversions += campaign.conversions || 0;
      totalRevenue += campaign.revenue || 0;
      totalImpressions += campaign.impressions || 0;
      totalGa4Revenue += campaign.ga4_total_revenue || 0;
      totalGa4Conversions += campaign.ga4_purchases || 0;
      totalSessions += campaign.ga4_sessions || 0;
      totalAdjustSessions += campaign.adjust_sessions || campaign.adjustSessions || 0;
    });

    const avgCpc = totalClicks > 0 ? totalCost / totalClicks : 0;
    const conversionRate = totalClicks > 0 ? totalConversions / totalClicks : 0;
    const roas = totalCost > 0 ? totalRevenue / totalCost : 0;
    const ga4Roas = totalCost > 0 ? totalGa4Revenue / totalCost : 0;
    const ga4ConversionRate = totalSessions > 0 ? totalGa4Conversions / totalSessions : 0;
    const ctr = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

    // All Available seçeneği için değişim oranlarını hesaplamıyoruz
    if (dateRange === 'all') {
      return {
        cpc: avgCpc,
        conversionRate,
        cost: totalCost,
        revenue: totalRevenue,
        roas,
        clicks: totalClicks,
        impressions: totalImpressions,
        ctr,
        conversions: totalConversions,
        ga4Revenue: totalGa4Revenue,
        ga4Roas,
        ga4Conversions: totalGa4Conversions,
        ga4ConversionRate,
        sessions: totalSessions,
        adjustSessions: totalAdjustSessions,
        cpcChange: 0,
        conversionRateChange: 0,
        costChange: 0,
        revenueChange: 0,
        roasChange: 0
      };
    }

    // Diğer tarih aralıkları için random değişim oranları (gerçekte API'den gelecek)
    const randomPercentage = () => (Math.random() * 50 - 25).toFixed(1);

    const cpcChange = parseFloat(randomPercentage());
    const conversionRateChange = parseFloat(randomPercentage());
    const costChange = parseFloat(randomPercentage());
    const revenueChange = parseFloat(randomPercentage());
    const roasChange = parseFloat(randomPercentage());

    return {
      cpc: avgCpc,
      conversionRate,
      cost: totalCost,
      revenue: totalRevenue,
      roas,
      clicks: totalClicks,
      impressions: totalImpressions,
      ctr,
      conversions: totalConversions,
      ga4Revenue: totalGa4Revenue,
      ga4Roas,
      ga4Conversions: totalGa4Conversions,
      ga4ConversionRate,
      sessions: totalSessions,
      adjustSessions: totalAdjustSessions,
      cpcChange,
      conversionRateChange,
      costChange,
      revenueChange,
      roasChange
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true); // Veri yüklenmeye başlarken true yap
        // Fetch keyword data from database
        await fetchKeywordData();
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to fetch campaign data');
      } finally {
        setDataLoading(false); // Veri yükleme bittiğinde false yap
      }
    };

    fetchData();
  }, [dateRange, selectedAccountId, gaAccountFilter]);

  // Re-fetch when custom range changes (second date picked)
  useEffect(() => {
    if (dateRange === 'custom' && customStartDate && customEndDate) {
      fetchKeywordData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customStartDate, customEndDate]);

  useEffect(() => {
    const brandId = localStorage.getItem('currentBrandId');
    if (!brandId) return;
    fetch('https://api.glowytics.com/api/auth/connections', {
      credentials: 'include',
      headers: { 'X-Brand-ID': brandId }
    })
      .then(r => r.json())
      .then(data => {
        const list = Array.isArray(data) ? data : (data.connections || []);
        const ga = list.filter(c => c.platform === 'GOOGLE_ADS' && c.status === 'ACTIVE');
        setGaAccounts(ga);
      })
      .catch(() => {});
  }, []);

  // Process keyword data when it changes
  useEffect(() => {
    if (keywordData && Array.isArray(keywordData) && keywordData.length > 0) {
      console.log('Processing keyword data...');
      try {
        const { campaigns: fetchedCampaigns } = organizeKeywordData(keywordData);

        // Update state with fetched data
        setCampaigns(fetchedCampaigns);

        // Başlangıçta tüm kampanyalar kapalı olsun
        setExpandedCampaigns(new Set());

        // Log the organized data
        console.log('Organized campaigns:', fetchedCampaigns);

        // Remove the call to generateTrendDataFromKeywords that's causing errors
        // generateTrendDataFromKeywords();

        setIsLoading(false);
      } catch (error) {
        console.error('Error processing keyword data:', error);
        // Remove the toast error message
        // toast.error('Error processing campaign data');
        setIsLoading(false);
      }
    }
  }, [keywordData]);

  // Function to fetch keyword data from the database
  const fetchKeywordData = async () => {
    try {
      setIsLoading(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoading(false);
        return;
      }

      console.log('Fetching keyword data for brand ID:', brandId);
      
      // Calculate date range based on selected filter
      const dateRangeParams = calculateDateRange(dateRange);
      console.log('Date range params:', dateRangeParams);
      
      // Custom tarih aralığı için start/end hesapla — tüm yan istekler de aynısını alacak
      const customSd = (dateRange === 'custom' && customStartDate) ? formatDateYMD(customStartDate) : null;
      const customEd = (dateRange === 'custom' && customEndDate) ? formatDateYMD(customEndDate) : null;
      const customSuffix = (customSd && customEd) ? `&startDate=${customSd}&endDate=${customEd}` : '';

      // Mevcut keyword veriyi çek
      let apiUrl = `https://api.glowytics.com/api/google-ads/keyword-full-performance?dateRange=${dateRange}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}${customSuffix}`;
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`Failed to fetch keyword data: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      console.log('Keyword data fetched successfully:', data);

      setKeywordData(data);

      // Yeni eklenen kısım: Performance Max Asset Groups verilerini çek
      await fetchPmaxAssetGroups(brandId, dateRange, customSd, customEd);

      // Product Group Performance verilerini çek
      await fetchProductGroupData(brandId, dateRange, customSd, customEd);
      
      // Try-catch block for fetchPerformanceTrend to prevent errors
      try {
        await fetchPerformanceTrend();
      } catch (error) {
        console.error("Error in fetchPerformanceTrend:", error);
      }
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching keyword data:', error);
      setIsLoading(false);
    }
  };

  // Product Group Performance (Shopping/Product groups) - new tables data source
  const fetchProductGroupData = async (brandId, dateRangeParam, customStartDateStr, customEndDateStr) => {
    try {
      const customSuffix = (dateRangeParam === 'custom' && customStartDateStr && customEndDateStr)
        ? `&startDate=${customStartDateStr}&endDate=${customEndDateStr}` : '';
      const resp = await fetch(`https://api.glowytics.com/api/google-ads/product-group-performance?dateRange=${dateRangeParam}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}${customSuffix}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });
      if (!resp.ok) {
        console.error('Failed to fetch product group performance');
        setProductGroupsRaw([]);
        setBrandSummary([]);
        setCampaignShoppingSummary([]);
        return;
      }
      const data = await resp.json();
      const rows = Array.isArray(data) ? data : [];
      setProductGroupsRaw(rows);
      // Category aggregation (prefer product_type_l1, then l2/l3)
      const catMap = new Map();
      rows.forEach(r => {
        const key = r.product_type_l1 || r.product_type_l2 || r.product_type_l3 || 'Uncategorized';
        if (!catMap.has(key)) {
          catMap.set(key, { category: key, impressions: 0, clicks: 0, cost: 0, conversions: 0 });
        }
        const m = catMap.get(key);
        m.impressions += Number(r.impressions || 0);
        m.clicks += Number(r.clicks || 0);
        m.cost += Number(r.cost || 0);
        m.conversions += Number(r.conversions || 0);
      });
      const catArr = Array.from(catMap.values()).map(m => ({
        ...m,
        ctr: m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0,
        cpc: m.clicks > 0 ? m.cost / m.clicks : 0,
      })).sort((a,b) => (b.cost || 0) - (a.cost || 0)).slice(0, 20);
      setCategorySummary(catArr);
      // Brand aggregation
      const brandMap = new Map();
      rows.forEach(r => {
        const key = r.product_brand || 'Unknown';
        if (!brandMap.has(key)) {
          brandMap.set(key, { product_brand: key, impressions: 0, clicks: 0, cost: 0, conversions: 0 });
        }
        const m = brandMap.get(key);
        m.impressions += Number(r.impressions || 0);
        m.clicks += Number(r.clicks || 0);
        m.cost += Number(r.cost || 0);
        m.conversions += Number(r.conversions || 0);
      });
      const brandArr = Array.from(brandMap.values()).map(m => ({
        ...m,
        ctr: m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0,
        cpc: m.clicks > 0 ? m.cost / m.clicks : 0,
      })).sort((a,b) => (b.cost || 0) - (a.cost || 0)).slice(0, 20);
      setBrandSummary(brandArr);

      // Campaign Shopping aggregation
      const campMap = new Map();
      rows.forEach(r => {
        const key = r.campaign_id || r.campaign_name || 'Unknown Campaign';
        if (!campMap.has(key)) {
          campMap.set(key, { campaign_id: r.campaign_id || key, campaign_name: r.campaign_name || key, impressions: 0, clicks: 0, cost: 0, conversions: 0, revenue: 0 });
        }
        const m = campMap.get(key);
        m.impressions += Number(r.impressions || 0);
        m.clicks += Number(r.clicks || 0);
        m.cost += Number(r.cost || 0);
        m.conversions += Number(r.conversions || 0);
        // Use all_conversions as revenue proxy
        m.revenue += Number(r.all_conversions || 0);
      });
      const campArr = Array.from(campMap.values()).map(m => ({
        ...m,
        ctr: m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0,
        cpc: m.clicks > 0 ? m.cost / m.clicks : 0,
      })).sort((a,b) => (b.cost || 0) - (a.cost || 0));
      setCampaignShoppingSummary(campArr);
    } catch (e) {
      console.error('fetchProductGroupData error:', e);
      setProductGroupsRaw([]);
      setCategorySummary([]);
      setBrandSummary([]);
      setCampaignShoppingSummary([]);
    }
  };
  
  // Performance Max campaign-level data (with Adjust) to enrich campaign rows
  const fetchPerformanceMaxCampaignsAdjust = async (brandId, dateRangeParam) => {
    try {
      const resp = await fetch(`https://api.glowytics.com/api/google-ads/performance-max-campaigns?dateRange=${dateRangeParam}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });
      if (!resp.ok) {
        console.warn('Failed to fetch performance max campaigns (adjust)');
        return {};
      }
      const data = await resp.json();
      const rows = Array.isArray(data?.campaigns) ? data.campaigns : Array.isArray(data) ? data : [];
      const map = {};
      rows.forEach(r => {
        const id = r.campaignId || r.campaign_id || r.id;
        if (!id) return;
        map[id] = {
          adjustSessions: Number(r.adjustSessions || r.adjust_sessions || 0),
          adjustInstalls: Number(r.adjustInstalls || r.adjust_installs || 0),
          adjustPurchaseEvents: Number(r.adjustPurchaseEvents || r.adjust_purchase_events || 0),
          adjustAllRevenue: Number(r.adjustAllRevenue || r.adjust_all_revenue || 0),
        };
      });
      return map;
    } catch (e) {
      console.warn('Error fetching performance max adjust data', e);
      return {};
    }
  };

  // Yeni eklediğimiz fetchPmaxAssetGroups fonksiyonu
  const fetchPmaxAssetGroups = async (brandId, dateRangeParam, customStartDateStr, customEndDateStr) => {
    try {
      console.log('Fetching Performance Max asset groups data for brand ID:', brandId);

      const customSuffix = (dateRangeParam === 'custom' && customStartDateStr && customEndDateStr)
        ? `&startDate=${customStartDateStr}&endDate=${customEndDateStr}` : '';
      const response = await fetch(`https://api.glowytics.com/api/google-ads/pmax-asset-groups?dateRange=${dateRangeParam}${gaAccountFilter !== 'all' ? '&accountId=' + gaAccountFilter : ''}${customSuffix}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        throw new Error(`Failed to fetch Performance Max asset groups: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();
      
      // Console'a tüm verileri yazdır
      console.log('=== PERFORMANCE MAX ASSET GROUPS DATA ===');
      console.log('Raw response:', data);
      console.log('Number of records:', data.length);
      
      // İlk 3 kaydı göster
      if (data.length > 0) {
        console.log('First 3 records:', data.slice(0, 3));
      }

      // Campaign-level Adjust metrics
      const adjustMap = await fetchPerformanceMaxCampaignsAdjust(brandId, dateRangeParam);
      
      // Verileri campaign ve asset group'lara göre organize edelim
      const campaignsMap = new Map();
      
      // Her veri satırını işleyelim
      data.forEach(item => {
        const campaignId = item.campaignId;
        const campaignName = item.campaignName;
        const assetGroupId = item.assetGroupId;
        const assetGroupName = item.assetGroupName;
        
        // Kampanya yoksa ekle
        if (!campaignsMap.has(campaignId)) {
          campaignsMap.set(campaignId, {
            id: campaignId,
            name: campaignName,
            status: '', // API'de kampanya durumu yok, o yüzden boş bırakıyoruz
            budget: 0, // API'de kampanya bütçesi yok
            spend: 0,
            impressions: 0,
            clicks: 0,
            conversions: 0,
            conversionValue: 0,
            adGroups: [],
            expanded: false // Başlangıçta kapalı olsun
          });
        }
        
        // Kampanyayı al
        const campaign = campaignsMap.get(campaignId);
        
        // Bu kampanyada bu asset group var mı kontrol et
        let assetGroup = campaign.adGroups.find(ag => ag.id === assetGroupId);
        
        // Yoksa ekle
        if (!assetGroup) {
          assetGroup = {
            id: assetGroupId,
            name: assetGroupName,
            status: item.assetGroupStatus.replace('AssetGroupStatus.', ''),
            campaignId: campaignId,
            spend: 0,
            impressions: 0,
            clicks: 0,
            conversions: 0,
            conversionValue: 0,
            keywords: [], // Asset group'larda keyword yok
            expanded: false
          };
          campaign.adGroups.push(assetGroup);
        }
        
        // Asset group metriklerini ekle
        assetGroup.impressions += item.impressions || 0;
        assetGroup.clicks += item.clicks || 0;
        assetGroup.spend += item.cost || 0;
        assetGroup.conversions += item.conversions || 0;
        assetGroup.conversionValue += item.conversionValue || 0;
        
        // Hesaplanan metrikleri güncelle
        assetGroup.ctr = assetGroup.impressions > 0 ? (assetGroup.clicks / assetGroup.impressions) * 100 : 0;
        assetGroup.cpc = assetGroup.clicks > 0 ? assetGroup.spend / assetGroup.clicks : 0;
        assetGroup.conversionRate = assetGroup.clicks > 0 ? (assetGroup.conversions / assetGroup.clicks) * 100 : 0;
        assetGroup.costPerConversion = assetGroup.conversions > 0 ? assetGroup.spend / assetGroup.conversions : 0;
        assetGroup.roas = assetGroup.spend > 0 ? assetGroup.conversionValue / assetGroup.spend : 0;
        
        // Kampanya metriklerini güncelle
        campaign.spend += item.cost || 0;
        campaign.impressions += item.impressions || 0;
        campaign.clicks += item.clicks || 0;
        campaign.conversions += item.conversions || 0;
        campaign.conversionValue += item.conversionValue || 0;
      });
      
      // Kampanya metriklerini hesapla
      campaignsMap.forEach(campaign => {
        campaign.ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions) * 100 : 0;
        campaign.cpc = campaign.clicks > 0 ? campaign.spend / campaign.clicks : 0;
        campaign.conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks) * 100 : 0;
        campaign.costPerConversion = campaign.conversions > 0 ? campaign.spend / campaign.conversions : 0;
        campaign.roas = campaign.spend > 0 ? campaign.conversionValue / campaign.spend : 0;
        campaign.revenue = campaign.conversionValue; // Geliri conversionValue olarak ayarla
        if (adjustMap[campaign.id]) {
          campaign.adjustSessions = adjustMap[campaign.id].adjustSessions;
          campaign.adjustInstalls = adjustMap[campaign.id].adjustInstalls;
          campaign.adjustPurchaseEvents = adjustMap[campaign.id].adjustPurchaseEvents;
          campaign.adjustAllRevenue = adjustMap[campaign.id].adjustAllRevenue;
        }
      });
      
      // Kampanyaları bir diziye dönüştür
      const campaigns = Array.from(campaignsMap.values());

      console.log('Organized Performance Max data:', { campaigns });
      console.log('=== END OF PERFORMANCE MAX ASSET GROUPS DATA ===');

      // Build per-(campaign, date) daily map for Segments breakdown rows.
      // Source: same asset_group_summary rows the table is built from, aggregated to campaign level.
      const dailyMap = new Map();
      data.forEach(item => {
        const campaignId = item.campaignId;
        const rawDate = item.date;
        if (!campaignId || !rawDate) return;
        const dateStr = typeof rawDate === 'string' ? rawDate.slice(0, 10) : new Date(rawDate).toISOString().slice(0, 10);
        const key = `${campaignId}_${dateStr}`;
        if (!dailyMap.has(key)) {
          dailyMap.set(key, {
            campaignId,
            date: dateStr,
            spend: 0,
            impressions: 0,
            clicks: 0,
            conversions: 0,
            conversionValue: 0,
          });
        }
        const d = dailyMap.get(key);
        d.spend += Number(item.cost || 0);
        d.impressions += Number(item.impressions || 0);
        d.clicks += Number(item.clicks || 0);
        d.conversions += Number(item.conversions || 0);
        d.conversionValue += Number(item.conversionValue || 0);
      });
      dailyMap.forEach(d => {
        d.ctr = d.impressions > 0 ? (d.clicks / d.impressions) * 100 : 0;
        d.cpc = d.clicks > 0 ? d.spend / d.clicks : 0;
        d.conversionRate = d.clicks > 0 ? (d.conversions / d.clicks) * 100 : 0;
        d.costPerConversion = d.conversions > 0 ? d.spend / d.conversions : 0;
        d.roas = d.spend > 0 ? d.conversionValue / d.spend : 0;
      });
      setDailyDataMap(dailyMap);

      // State'i güncelle
      setPerformanceMaxData({ campaigns });
      setCampaigns(campaigns); // Mevcut campaign state'ini de güncelleyelim
      
      // Daily cost trend için veri hazırla
      // Tüm günleri içeren bir map oluştur
      const dailyData = new Map();
      
      // Tarih aralığını hesapla
      const { startDate, endDate } = calculateDateRange(dateRangeParam || dateRange);
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      // Tüm günleri sıfır değerlerle doldur
      for (let day = new Date(start); day <= end; day.setDate(day.getDate() + 1)) {
        const dateStr = day.toISOString().split('T')[0];
        dailyData.set(dateStr, { date: dateStr, cost: 0, revenue: 0 });
      }
      
      // API yanıtından gelen her öğe için:
      data.forEach(item => {
        const dateStr = item.date.split('T')[0];
        if (dailyData.has(dateStr)) {
          const dayData = dailyData.get(dateStr);
          dayData.cost += parseFloat(item.cost || 0);
          dayData.revenue += parseFloat(item.conversionValue || 0);
        }
      });
      
      // Map'i bir diziye dönüştür ve tarihe göre sırala
      const dailyTrendData = Array.from(dailyData.values()).sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );
      
      console.log('Daily trend data:', dailyTrendData);
      
      // Chart verilerini güncelle
      updateDailyCostChart(dailyTrendData);
      
      return data;
    } catch (error) {
      console.error('Error fetching Performance Max asset groups:', error);
      return [];
    }
  };
  
  // calculateDateRange fonksiyonunu düzenleyelim
  const calculateDateRange = (selectedRange) => {
    const now = new Date();
    let startDate, endDate;

    switch (selectedRange) {
      case 'all':
        // Çok eski bir tarih (örneğin 5 yıl önce) başlangıç olarak alınabilir
        startDate = new Date(now.getFullYear() - 5, 0, 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'today':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'yesterday':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59);
        break;
      case 'last7':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'last30':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'thisMonth':
        // First day of current month
        startDate = new Date(now.getFullYear(), now.getMonth(), 1);
        // Current day
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        break;
      case 'lastMonth':
        // First day of last month
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        // Last day of last month
        endDate = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);
        break;
      case 'custom':
        // Özel tarih aralığı seçilmişse, bu tarihleri kullan
        if (customStartDate && customEndDate) {
          startDate = new Date(customStartDate);
          // Bitiş tarihini günün sonuna ayarla
          endDate = new Date(customEndDate);
          endDate.setHours(23, 59, 59);
        } else {
          // Eğer custom tarihler ayarlanmamışsa, son 7 günü göster
          startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
          endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
        }
        break;
      default:
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
        endDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
    }

    return {
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0]
    };
  };
  
  // Helper function to filter data by date range
  const filterDataByDateRange = (data, dateRange) => {
    if (!data || !Array.isArray(data) || data.length === 0) {
      return [];
    }
    
    const startDate = new Date(dateRange.startDate);
    const endDate = new Date(dateRange.endDate);
    
    // Set time to end of day for end date for inclusive comparison
    endDate.setHours(23, 59, 59, 999);
    
    return data.filter(item => {
      if (!item.date) return false;
      
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= endDate;
    });
  };
  
  // New function to group keyword data by date and calculate total cost
  const groupKeywordDataByDate = (keywordData) => {
    console.log("========== STARTING DAILY COST CALCULATION ==========");
    console.log("Input data count:", keywordData.length);
    
    // Log the structure of the first few records to understand the data format
    if (keywordData.length > 0) {
      // Log the keys of the first record to see what fields are available
      console.log("First record keys:", Object.keys(keywordData[0]));
      
      // Log the complete first record for inspection
      console.log("First record:", keywordData[0]);
      
      // Check if cost is in a different field
      if (keywordData[0].cost !== undefined) {
        console.log("Found 'cost' field instead of 'cost_micros'");
      }
      
      // Check for nested fields that might contain cost data
      for (const key in keywordData[0]) {
        const value = keywordData[0][key];
        if (value && typeof value === 'object') {
          console.log(`Checking nested object in field '${key}':`, value);
          
          // Check if this nested object has cost-related fields
          if (value.cost !== undefined || value.cost_micros !== undefined) {
            console.log(`Found cost data in nested field '${key}'`);
          }
        }
      }
    }
    
    // Create a map to store costs by date
    const costByDate = new Map();
    let processedRecords = 0;
    let recordsWithDate = 0;
    let recordsWithCost = 0;
    
    // Process each keyword
    keywordData.forEach(keyword => {
      processedRecords++;
      
      // Check if the keyword has a date field
      if (keyword.date) {
        recordsWithDate++;
        
        // Check for cost field (not cost_micros)
        if (keyword.cost !== undefined) {
          recordsWithCost++;
          const date = keyword.date;
          // Assuming cost is already in the correct format (not micros)
          const cost = parseFloat(keyword.cost);
          
          if (costByDate.has(date)) {
            costByDate.set(date, costByDate.get(date) + cost);
          } else {
            costByDate.set(date, cost);
          }
        }
      }
    });
    
    console.log("Processing stats:");
    console.log("- Total records processed:", processedRecords);
    console.log("- Records with date field:", recordsWithDate);
    console.log("- Records with cost field:", recordsWithCost);
    
    // Convert map to array of objects sorted by date
    const dailyCostArray = Array.from(costByDate.entries())
      .map(([date, cost]) => ({ date, cost }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
    
    console.log("========== DAILY COST DATA (GROUPED BY DATE) ==========");
    console.log(dailyCostArray);
    
    // Also log in table format for better readability
    console.log("========== DAILY COST DATA (TABLE FORMAT) ==========");
    console.table(dailyCostArray);
    
    console.log("Total unique dates:", dailyCostArray.length);
    console.log("Date range:", 
      dailyCostArray.length > 0 ? 
      `${dailyCostArray[0].date} to ${dailyCostArray[dailyCostArray.length-1].date}` : 
      "No data"
    );
    console.log("Total cost:", dailyCostArray.reduce((sum, item) => sum + item.cost, 0).toFixed(2));
    console.log("========== END OF DAILY COST CALCULATION ==========");
    
    // Update the chart with daily cost data
    updateDailyCostChart(dailyCostArray);
    
    return dailyCostArray;
  };
  
  // New function to update the chart with daily cost data
  const updateDailyCostChart = (dailyData) => {
    const labels = dailyData.map(day => {
      // Formatlanmış tarih: "03 Apr" gibi
      const date = new Date(day.date);
      return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short' });
    });
    
    const costData = dailyData.map(day => day.cost);
    const revenueData = dailyData.map(day => day.revenue);
    
    const newChartData = {
      labels,
      datasets: [
        {
          label: 'Spend',
          data: costData,
          borderColor: 'rgb(59, 130, 246)', // blue
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: 'rgb(16, 185, 129)', // green
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
    
    // Chart options
    const newChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          align: 'end',
          labels: {
            boxWidth: 10,
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'TRY',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(context.parsed.y);
              }
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              return '₺' + value.toLocaleString();
            }
          },
          grid: {
            borderDash: [2, 4],
            color: 'rgba(0, 0, 0, 0.06)'
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };
    
    setChartData(newChartData);
    setChartOptions(newChartOptions);
  };
  
  // Performans trend verilerini çekmek için yeni fonksiyon
  const fetchPerformanceTrend = async () => {
    try {
      setIsLoadingTrend(true);
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) {
        console.error('No brand ID found in localStorage');
        setIsLoadingTrend(false);
        return;
      }
      
      // Use fetch API instead of axios
      const trendUrl = `https://api.glowytics.com/api/google-ads/performance-trend${gaAccountFilter !== 'all' ? '?accountId=' + gaAccountFilter : ''}`;
      const response = await fetch(trendUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'X-Brand-ID': brandId
        },
        credentials: 'include',
      });

      if (!response.ok) {
        // Don't try to parse JSON if response is not OK
        console.error('API Error:', response.status, response.statusText);
        throw new Error(`Failed to fetch performance trend: ${response.statusText}`);
      }

      const data = await response.json();
      console.log("API Performance Trend Response:", data);

      if (data && data.trend) {
        setTrendData(data.trend);
      } else {
        setTrendData(generateEmptyTrendData());
      }
      setIsLoadingTrend(false);
    } catch (error) {
      console.error("Error fetching performance trend:", error);
      setTrendData(generateEmptyTrendData());
      setIsLoadingTrend(false);
      // Don't show error toast
    }
  };

  // Keyword verilerinden trend verilerini oluşturan yardımcı fonksiyon
  const generateTrendDataFromKeywords = () => {
    try {
      if (!keywordData || !Array.isArray(keywordData) || keywordData.length === 0) {
        console.log("No keyword data available for trend generation");
        return;
      }

      // Create a map to store metrics by date
      const metricsByDate = new Map();

      // Process all keywords directly from keywordData
      keywordData.forEach(keyword => {
        if (keyword.date) {
          const date = keyword.date;
          
          if (!metricsByDate.has(date)) {
            metricsByDate.set(date, {
              date,
              impressions: 0,
              clicks: 0,
              cost: 0,
              conversions: 0,
              conversionValue: 0
            });
          }

          const metrics = metricsByDate.get(date);
          metrics.impressions += parseInt(keyword.impressions || 0, 10);
          metrics.clicks += parseInt(keyword.clicks || 0, 10);
          metrics.cost += parseFloat(keyword.cost || 0);
          metrics.conversions += parseInt(keyword.conversions || 0, 10);
          metrics.conversionValue += parseFloat(keyword.conversion_value || 0);
        }
      });

      // Convert to array and sort by date
      const trendData = Array.from(metricsByDate.values())
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      // Calculate derived metrics
      trendData.forEach(day => {
        day.ctr = day.impressions > 0 ? (day.clicks / day.impressions) * 100 : 0;
        day.cpc = day.clicks > 0 ? day.cost / day.clicks : 0;
        day.convRate = day.clicks > 0 ? (day.conversions / day.clicks) * 100 : 0;
        day.cpa = day.conversions > 0 ? day.cost / day.conversions : 0;
        day.roas = day.cost > 0 ? day.conversionValue / day.cost : 0;
      });

      console.log("Generated trend data from keywords:", trendData);
      
      return trendData;
    } catch (error) {
      console.error("Error generating trend data:", error);
      return [];
    }
  };
  
  // Boş trend verisi oluşturan yardımcı fonksiyon
  const generateEmptyTrendData = () => {
    console.log("Generating empty trend data for the last 30 days");
    
    // Son 30 günün tarihlerini oluştur
    const labels = [];
    const today = new Date();
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(today.getDate() - i);
      // Tarihi gün-ay formatında göster
      labels.push(date.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' }));
    }
    
    return {
      labels: labels,
      datasets: [
        {
          label: 'Spend',
          data: Array(30).fill(0),
          borderColor: 'rgb(59, 130, 246)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        },
        {
          label: 'Revenue',
          data: Array(30).fill(0),
          borderColor: 'rgb(16, 185, 129)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y'
        }
      ]
    };
  };

  // Find and update the formatNumber function or any other function that formats currency values
  const formatNumber = (value, options = {}) => {
    if (value === undefined || value === null) return '-';
    
    // Özel formatlar için opsiyonları kontrol et
    const {
      format = 'decimal',
      precision = 2,
      withCurrency = false,
      currencySymbol = '₺',
      compact = false
    } = options;
    
    // Değeri sayı olarak emin olalım
    const numValue = Number(value);
    
    // NaN kontrolü
    if (isNaN(numValue)) return '-';
    
    // Yüzde formatı - Conversion Rate için
    if (format === 'percent') {
      console.log("Formatting as percent:", numValue); // Debug
      // Conversion Rate değeri genellikle ondalık olarak gelir (örn: 0.62)
      // Bunu yüzde olarak gösterelim (örn: 62%)
      const percentValue = (numValue * 100).toFixed(precision);
      console.log("Percent value:", percentValue); // Debug
      return `%${percentValue}`;
    }
    
    // Para birimi formatı
    if (format === 'currency') {
      if (compact && numValue >= 1000000) {
        return `${currencySymbol}${(numValue / 1000000).toFixed(1)}M`;
      } else if (compact && numValue >= 1000) {
        return `${currencySymbol}${(numValue / 1000).toFixed(1)}K`;
      }
      return `${currencySymbol}${numValue.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: precision })}`;
    }
    
    // Ondalık formatı
    if (compact && numValue >= 1000000) {
      return `${(numValue / 1000000).toFixed(1)}M`;
    } else if (compact && numValue >= 1000) {
      return `${(numValue / 1000).toFixed(1)}K`;
    }
    
    return numValue.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: precision });
  };

  // Get summary metrics
  const summaryMetrics = calculateSummaryMetrics(campaigns);
  const cps = summaryMetrics.sessions > 0 ? (summaryMetrics.cost || 0) / summaryMetrics.sessions : 0;

  // useEffect içinde, sayfa yüklendiğinde varsayılan olarak budget'a göre sırala
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Veri çekme işlemleri...
        
        // Varsayılan olarak budget'a göre azalan sıralama yap
        setSortField('budget');
        setSortDirection('desc');
        
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  // Kampanyaları sırala
  const sortedCampaigns = [...campaigns].sort((a, b) => {
    // Sayısal değerler için sıralama
    if ([
      'budget',
      'impressions',
      'clicks',
      'ctr',
      'cpc',
      'conversions',
      'conversionRate',
      'costPerConversion',
      'conversionValue',
      'roas',
      'adjustSessions',
      'adjustInstalls',
      'adjustPurchaseEvents',
      'adjustAllRevenue'
    ].includes(sortField)) {
      // Sayısal değerleri güvenli bir şekilde al
      const aValue = parseFloat(
        a[sortField] ??
        (sortField === 'adjustSessions' ? a.adjust_sessions : undefined) ??
        (sortField === 'adjustInstalls' ? a.adjust_installs : undefined) ??
        (sortField === 'adjustPurchaseEvents' ? a.adjust_purchase_events : undefined) ??
        (sortField === 'adjustAllRevenue' ? a.adjust_all_revenue : undefined) ??
        0
      ) || 0;
      const bValue = parseFloat(
        b[sortField] ??
        (sortField === 'adjustSessions' ? b.adjust_sessions : undefined) ??
        (sortField === 'adjustInstalls' ? b.adjust_installs : undefined) ??
        (sortField === 'adjustPurchaseEvents' ? b.adjust_purchase_events : undefined) ??
        (sortField === 'adjustAllRevenue' ? b.adjust_all_revenue : undefined) ??
        0
      ) || 0;
      
      return sortDirection === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    }
    
    // Metin değerleri için sıralama (kampanya adı, durum vb.)
    if (['name', 'status', 'text'].includes(sortField)) {
      const aValue = String(a[sortField] || '').toLowerCase();
      const bValue = String(b[sortField] || '').toLowerCase();
      
      if (sortDirection === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    }
    
    // Varsayılan olarak budget'a göre azalan sıralama
    return b.budget - a.budget;
  });

  // Adjust verisi var mı kontrol et
  const hasAdjust = useMemo(() => {
    return sortedCampaigns.some(c =>
      ((c.adjustSessions || c.adjust_sessions || 0) +
        (c.adjustInstalls || c.adjust_installs || 0) +
        (c.adjustPurchaseEvents || c.adjust_purchase_events || 0) +
        (c.adjustAllRevenue || c.adjust_all_revenue || 0)) > 0
    );
  }, [sortedCampaigns]);

  // Ad Group'ları sırala
  const sortAdGroups = (adGroups, field, direction) => {
    if (!field) return adGroups;

    return [...adGroups].sort((a, b) => {
      // Sayısal değerler için sıralama
      if (['budget', 'impressions', 'clicks', 'ctr', 'cpc', 'conversions', 'conversionRate', 'costPerConversion', 'conversionValue', 'roas'].includes(field)) {
        // Sayısal değerleri güvenli bir şekilde al
        const aValue = parseFloat(a[field]) || 0;
        const bValue = parseFloat(b[field]) || 0;
        
        return direction === 'asc' 
          ? aValue - bValue 
          : bValue - aValue;
      }
      
      // Metin değerleri için sıralama (ad group adı, durum vb.)
      if (['name', 'status', 'text'].includes(field)) {
        const aValue = String(a[field] || '').toLowerCase();
        const bValue = String(b[field] || '').toLowerCase();
        
        if (direction === 'asc') {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      }
      
      // Varsayılan olarak budget'a göre azalan sıralama
      return b.budget - a.budget;
    });
  };

  // (customDatePicker + customStartDate/customEndDate + dateDropdown state
  //  hoisted to the top of the component to avoid TDZ — see earlier block.)

  // Click-outside handler ekleyin
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isDateDropdownOpen) {
        setIsDateDropdownOpen(false);
      }
      if (isSegmentDropdownOpen) {
        setIsSegmentDropdownOpen(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDateDropdownOpen, isSegmentDropdownOpen]);

  // Aylık veri için chart güncelleme fonksiyonu ekleyelim
  const updateMonthlyChart = (monthlyData) => {
    // Ay-yıl formatında etiketler
    const labels = monthlyData.map(month => month.displayDate);
    
    const costData = monthlyData.map(month => month.cost);
    const revenueData = monthlyData.map(month => month.revenue);
    
    const newChartData = {
      labels,
      datasets: [
        {
          label: 'Spend',
          data: costData,
          borderColor: 'rgb(59, 130, 246)', // blue
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4
        },
        {
          label: 'Revenue',
          data: revenueData,
          borderColor: 'rgb(16, 185, 129)', // green
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4
        }
      ]
    };
    
    // Chart options - aylık görünüm için optimize edilmiş
    const newChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: 'Monthly Performance',
          align: 'start',
          font: {
            size: 14,
            weight: '500'
          },
          padding: {
            bottom: 15
          },
          color: '#374151'
        },
        legend: {
          position: 'top',
          align: 'end',
          labels: {
            boxWidth: 10,
            usePointStyle: true,
            pointStyle: 'circle'
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          callbacks: {
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'TRY',
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0
                }).format(context.parsed.y);
              }
              return label;
            }
          }
        }
      },
      scales: {
        x: {
          grid: {
            display: false
          }
        },
        y: {
          beginAtZero: true,
          ticks: {
            callback: function(value) {
              // Büyük sayılar için kısaltılmış format (1.2M, 450K gibi)
              if (value >= 1000000) {
                return '₺' + (value / 1000000).toFixed(1) + 'M';
              } else if (value >= 1000) {
                return '₺' + (value / 1000).toFixed(1) + 'K';
              } else {
                return '₺' + value;
              }
            }
          },
          grid: {
            borderDash: [2, 4],
            color: 'rgba(0, 0, 0, 0.06)'
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };
    
    setChartData(newChartData);
    setChartOptions(newChartOptions);
  };

  // MetricChange bileşenini güncelleyelim
  const MetricChange = ({ value, dateRangeType }) => {
    // All Available seçildiğinde karşılaştırma yapamayız
    if (dateRangeType === 'all') {
      return (
        <div className="flex items-center">
          <span className="text-xs text-gray-500">No data to compare</span>
        </div>
      );
    }
    
    // Değer pozitifse yeşil yukarı ok, negatifse kırmızı aşağı ok
    const isPositive = value > 0;
    
    return (
      <div className="flex items-center gap-1">
        {isPositive ? (
          <TrendingUp className={`w-3 h-3 ${value > 0 ? 'text-green-500' : 'text-gray-400'}`} />
        ) : (
          <TrendingDown className={`w-3 h-3 ${value < 0 ? 'text-red-500' : 'text-gray-400'}`} />
        )}
        <span 
          className={`text-xs font-medium ${
            isPositive 
              ? 'text-green-600' 
              : value < 0 
                ? 'text-red-600' 
                : 'text-gray-500'
          }`}
        >
          {Math.abs(value)}% vs prev. period
        </span>
      </div>
    );
  };

  // Custom date range seçimi için fonksiyon ekleyin
  const handleCustomDateSelect = () => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      // API çağrısı için tarihleri formatlayın
      const formattedStartDate = formatDateYMD(customStartDate);
      const formattedEndDate = formatDateYMD(customEndDate);
      // Burada API çağrısı yapabilirsiniz
      fetchKeywordData(formattedStartDate, formattedEndDate);
    }
    setShowCustomDatePicker(false);
    setIsDateDropdownOpen(false);
  };

  // Per-campaign daily breakdown for the selected segment (7/14/30 days).
  const getDailyDataForCampaign = (campaignId) => {
    const daysToShow = selectedSegment === '7days' ? 7
      : selectedSegment === '14days' ? 14
      : selectedSegment === '30days' ? 30
      : 0;
    if (daysToShow === 0) return [];

    let endDate;
    switch (dateRange) {
      case 'yesterday': {
        const y = new Date();
        y.setDate(y.getDate() - 1);
        endDate = y;
        break;
      }
      case 'custom':
        endDate = customEndDate ? new Date(customEndDate) : new Date();
        break;
      default:
        endDate = new Date();
    }

    const segmentEndDate = new Date(endDate);
    const segmentStartDate = new Date(segmentEndDate);
    segmentStartDate.setDate(segmentEndDate.getDate() - daysToShow + 1);

    const records = [];
    const current = new Date(segmentStartDate);
    while (current <= segmentEndDate) {
      const dateStr = current.toISOString().split('T')[0];
      const key = `${campaignId}_${dateStr}`;
      if (dailyDataMap.has(key)) {
        records.push(dailyDataMap.get(key));
      } else {
        records.push({
          campaignId,
          date: dateStr,
          spend: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversionValue: 0,
          ga4_sessions: 0,
          ga4_purchases: 0,
          ga4_total_revenue: 0,
          adjust_sessions: 0,
          adjust_installs: 0,
          adjust_purchase_events: 0,
          adjust_all_revenue: 0,
          ctr: 0,
          cpc: 0,
          conversionRate: 0,
          costPerConversion: 0,
          roas: 0,
          ga4_roas: 0,
          isEmpty: true,
        });
      }
      current.setDate(current.getDate() + 1);
    }

    const hasAnyData = records.some(r => !r.isEmpty);
    if (!hasAnyData) {
      const all = Array.from(dailyDataMap.values()).filter(d => d.campaignId === campaignId);
      if (all.length > 0) {
        return all.sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, daysToShow);
      }
    }
    return records.sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  // Per-campaign monthly aggregation (YYYY-MM buckets).
  const getMonthlyDataForCampaign = (campaignId) => {
    const monthsMap = new Map();
    const allCampaignData = Array.from(dailyDataMap.values()).filter(d => d.campaignId === campaignId);
    if (allCampaignData.length === 0) return [];

    for (const d of allCampaignData) {
      const monthKey = (d.date || '').slice(0, 7);
      if (!monthKey) continue;
      if (!monthsMap.has(monthKey)) {
        monthsMap.set(monthKey, {
          campaignId,
          date: monthKey,
          spend: 0,
          impressions: 0,
          clicks: 0,
          conversions: 0,
          conversionValue: 0,
          ga4_sessions: 0,
          ga4_purchases: 0,
          ga4_total_revenue: 0,
          adjust_sessions: 0,
          adjust_installs: 0,
          adjust_purchase_events: 0,
          adjust_all_revenue: 0,
        });
      }
      const m = monthsMap.get(monthKey);
      m.spend += d.spend || 0;
      m.impressions += d.impressions || 0;
      m.clicks += d.clicks || 0;
      m.conversions += d.conversions || 0;
      m.conversionValue += d.conversionValue || 0;
      m.ga4_sessions += d.ga4_sessions || 0;
      m.ga4_purchases += d.ga4_purchases || 0;
      m.ga4_total_revenue += d.ga4_total_revenue || 0;
      m.adjust_sessions += d.adjust_sessions || 0;
      m.adjust_installs += d.adjust_installs || 0;
      m.adjust_purchase_events += d.adjust_purchase_events || 0;
      m.adjust_all_revenue += d.adjust_all_revenue || 0;
    }
    for (const m of monthsMap.values()) {
      m.ctr = m.impressions > 0 ? (m.clicks / m.impressions) * 100 : 0;
      m.cpc = m.clicks > 0 ? m.spend / m.clicks : 0;
      m.conversionRate = m.clicks > 0 ? (m.conversions / m.clicks) * 100 : 0;
      m.costPerConversion = m.conversions > 0 ? m.spend / m.conversions : 0;
      m.roas = m.spend > 0 ? m.conversionValue / m.spend : 0;
      m.ga4_roas = m.spend > 0 ? m.ga4_total_revenue / m.spend : 0;
      m.cps = m.ga4_sessions > 0 ? m.spend / m.ga4_sessions : 0;
    }
    return Array.from(monthsMap.values()).sort((a, b) => b.date.localeCompare(a.date));
  };

  return (
    <div className="p-6 space-y-6 bg-[#f6f8ff]/70">
      {/* Header */}
      <div className="flex justify-between items-center bg-white p-6 rounded-2xl shadow-sm">
        <div className="flex items-center gap-3">
          {/* Google Ads logosu yerine performancemax.svg ikonumuzu koyuyoruz */}
          <img 
            src="/performancemax.svg" 
            alt="Performance Max Campaigns" 
            className="w-10 h-10 object-contain"
          />
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Performance Max</h1>
            <p className="text-sm text-gray-500 mt-1">Manage and monitor your automated multi-channel campaign performance</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          {/* Account Switcher */}
          <AccountSwitcher platform="GOOGLE_ADS" />

          {/* Seçili tarih aralığı - yeni eklenen kısım */}
          <div className="text-sm text-gray-500">
            {(() => {
              switch (dateRange) {
                case 'today':
                  return new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'yesterday':
                  const yesterday = new Date();
                  yesterday.setDate(yesterday.getDate() - 1);
                  return yesterday.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
                case 'last7':
                  const last7 = new Date();
                  last7.setDate(last7.getDate() - 7);
                  return `${last7.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'last30':
                  const last30 = new Date();
                  last30.setDate(last30.getDate() - 30);
                  return `${last30.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'thisMonth':
                  const firstDay = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
                  return `${firstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${new Date().toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'lastMonth':
                  const lastMonthFirstDay = new Date(new Date().getFullYear(), new Date().getMonth() - 1, 1);
                  const lastMonthLastDay = new Date(new Date().getFullYear(), new Date().getMonth(), 0);
                  return `${lastMonthFirstDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${lastMonthLastDay.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                case 'custom':
                  if (customStartDate && customEndDate) {
                    return `${customStartDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })} - ${customEndDate.toLocaleDateString('en-US', { day: 'numeric', month: 'short' })}`;
                  }
                  return 'Custom Range';
                default:
                  return '';
              }
            })()}
          </div>

          {/* Refresh button */}
          <button 
            onClick={fetchKeywordData}
            className={`p-2 text-gray-600 hover:text-blue-600 rounded-xl hover:bg-blue-50 ${isLoading ? 'animate-spin' : ''}`}
          >
            <RefreshCw className="w-5 h-5" />
          </button>

          {/* Google Ads Account Filter */}
          {gaAccounts.length >= 2 && (
            <select
              value={gaAccountFilter}
              onChange={(e) => setGaAccountFilter(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-xl text-sm bg-white shadow-sm hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              <option value="all">All Accounts</option>
              {gaAccounts.map(acc => (
                <option key={acc.customerId} value={acc.customerId}>
                  {acc.nickname || acc.accountName || acc.customerId}
                </option>
              ))}
            </select>
          )}

          {/* Unified date range filter (replaces legacy dropdown + modal) */}
          <DateRangeFilter
            value={dateRange}
            customStart={customStartDate}
            customEnd={customEndDate}
            onChange={(range, start, end) => {
              setDateRange(range);
              setCustomStartDate(start);
              setCustomEndDate(end);
            }}
            presets={['yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom']}
          />

          {/* Export button */}
          <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700">
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
        </div>
      </div>

      <Ga4WebOnlyNote />

      {/* Performance Overview */}
      <div className="grid grid-cols-12 gap-6">
        {/* Chart */}
        <Card className="col-span-8 rounded-2xl overflow-hidden bg-white">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {dateRange === 'all' ? 'Monthly Cost Trend' : 'Daily Cost Trend'}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  {dateRange === 'all' ? 
                    'Campaign monthly cost distribution' : 
                    'Campaign daily cost distribution'
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                  <span className="text-xs font-medium text-gray-600">Spend</span>
                </div>
                <div className="flex items-center gap-1.5 ml-4">
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-xs font-medium text-gray-600">Revenue</span>
                </div>
              </div>
            </div>
          </div>
          <div className="p-6">
            <div className="h-[300px]">
              {isLoading ? (
                <div className="h-full flex items-center justify-center">
                  <div className="loading-logo-container">
                    <img 
                      src="/logoicon.png" 
                      alt="Glowytics Logo" 
                      className="loading-logo"
                    />
                  </div>
                </div>
              ) : (
                <Line data={chartData} options={chartOptions} />
              )}
            </div>
          </div>
        </Card>

        {/* Key Metrics - kartların sırasını değiştirdik */}
        <div className="col-span-1 lg:col-span-4 grid grid-cols-1 gap-3 min-w-0">
          {/* Row 1: Revenue, GA4 Revenue */}
          <div className="grid grid-cols-2 gap-3 min-w-0">
            {/* Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Revenue</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={(!isNaN(summaryMetrics.revenue) && summaryMetrics.revenue ? Math.floor(summaryMetrics.revenue).toLocaleString('tr-TR') : '0')}>
                  ₺{(!isNaN(summaryMetrics.revenue) && summaryMetrics.revenue ? Math.floor(summaryMetrics.revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.roas || 0)}x ROAS</p>
              </div>
            </div>
            {/* GA4 Revenue Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <div className="text-gray-500 text-xs flex items-center gap-1">
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
                  <span>Revenue</span>
                </div>
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={(!isNaN(summaryMetrics.ga4Revenue) && summaryMetrics.ga4Revenue ? Math.floor(summaryMetrics.ga4Revenue).toLocaleString('tr-TR') : '0')}>
                  ₺{(!isNaN(summaryMetrics.ga4Revenue) && summaryMetrics.ga4Revenue ? Math.floor(summaryMetrics.ga4Revenue).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">{formatNumber(summaryMetrics.ga4Roas || 0)}x ROAS</p>
              </div>
            </div>
          </div>

          {/* Row 2: Cost, Conversions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Cost Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Cost</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={(!isNaN(summaryMetrics.cost) && summaryMetrics.cost ? Math.floor(summaryMetrics.cost).toLocaleString('tr-TR') : '0')}>
                  ₺{(!isNaN(summaryMetrics.cost) && summaryMetrics.cost ? Math.floor(summaryMetrics.cost).toLocaleString('tr-TR') : '0')}
                </h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Conversions Card (Combined) */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Conversions</p>
                <div className="flex items-center gap-1">
                  <img src="/google_ads-512.webp" alt="Google Ads" className="w-3 h-3" />
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                </div>
              </div>
              <div className="flex items-center justify-around mt-2">
                {/* Ads Data */}
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800">{summaryMetrics.conversions || 0}</p>
                  <p className="text-[10px] text-gray-500">%{formatNumber(summaryMetrics.conversionRate ? summaryMetrics.conversionRate * 100 : 0, {maximumFractionDigits: 2})} CR</p>
                </div>
                {/* Separator */}
                <div className="h-10 border-l border-gray-200"></div>
                {/* GA4 Data */}
                <div className="text-center">
                  <p className="text-xl font-bold text-gray-800">{summaryMetrics.ga4Conversions || 0}</p>
                  <p className="text-[10px] text-gray-500">%{formatNumber(summaryMetrics.ga4ConversionRate ? summaryMetrics.ga4ConversionRate * 100 : 0, {maximumFractionDigits: 2})} CR</p>
                </div>
              </div>
            </div>
          </div>

          {/* Row 3: Clicks, Sessions */}
          <div className="grid grid-cols-2 gap-3">
            {/* Clicks Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Clicks</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.clicks || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.clicks || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* Sessions Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">Sessions</p>
                {hasAdjust ? (
                  <div className="flex items-center gap-1">
                    <img src="/adjustlogopng.png" alt="Adjust" className="w-3 h-3" />
                    <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                  </div>
                ) : (
                  <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
                )}
              </div>
              {hasAdjust ? (
                <div className="flex items-center justify-around mt-2">
                  {/* Adjust Sessions */}
                  <div className="text-center w-full min-w-0">
                    <p className="text-xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.adjustSessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.adjustSessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                    <p className="text-[10px] text-gray-500">Adj. Sessions</p>
                  </div>
                  {/* Separator */}
                  <div className="h-10 border-l border-gray-200"></div>
                  {/* GA4 Sessions */}
                  <div className="text-center w-full min-w-0">
                    <p className="text-xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</p>
                    <div className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
                      <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                      <span>Sessions</span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="mt-2 text-center">
                  <h3 className="text-2xl font-bold text-gray-800 truncate" title={formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}>{formatNumber(summaryMetrics.sessions || 0, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</h3>
                  <div className="flex items-center justify-center gap-1 text-[10px] text-gray-500">
                    <img src="/GA4logoicon.svg" alt="GA4" className="w-3 h-3" />
                    <span>Sessions</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 4: CPC, CPS */}
          <div className="grid grid-cols-2 gap-3">
            {/* CPC Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPC</p>
                <img src="/google_ads-512.webp" alt="Google Ads" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{(summaryMetrics.cpc || 0).toFixed(2)}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
            {/* CPS Card */}
            <div className="bg-white rounded-xl shadow-sm p-3">
              <div className="flex justify-between items-center">
                <p className="text-gray-500 text-xs">CPS</p>
                <img src="/GA4logoicon.svg" alt="GA4" className="w-4 h-4" />
              </div>
              <div className="mt-2 text-center">
                <h3 className="text-2xl font-bold text-gray-800">₺{cps.toFixed(2)}</h3>
                <p className="text-xs text-gray-500">&nbsp;</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Campaign Performance Table with Ad Groups */}
      <Card className="rounded-2xl overflow-hidden bg-white">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Performance Max Campaign Performance</h2>
              <p className="text-sm text-gray-500 mt-1">
                {selectedSegment === 'monthly'
                  ? 'Monthly breakdown for campaigns'
                  : selectedSegment !== 'none'
                  ? `Daily breakdown for campaigns (${selectedSegment === '7days' ? '7' : selectedSegment === '14days' ? '14' : '30'} days)`
                  : 'Detailed metrics for campaigns and asset groups'}
              </p>
            </div>

            {/* Segments Dropdown */}
            <div className="relative inline-block">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsSegmentDropdownOpen(!isSegmentDropdownOpen);
                }}
                className="flex items-center gap-2 px-4 py-2.5 bg-gray-50 border rounded-xl text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <LayoutList className="w-4 h-4 text-gray-400" />
                <span className="text-sm font-medium">
                  {selectedSegment === 'none' && 'Segments'}
                  {selectedSegment === '7days' && '7 Days'}
                  {selectedSegment === '14days' && '14 Days'}
                  {selectedSegment === '30days' && '30 Days'}
                  {selectedSegment === 'monthly' && 'Monthly'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>

              {isSegmentDropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-200 py-2 z-50">
                  <div className="px-3 pb-2 mb-2 border-b border-gray-100">
                    <h3 className="text-xs font-medium text-gray-500 uppercase">Date Segments</h3>
                  </div>
                  {[
                    { v: 'none', label: 'None' },
                    { v: '7days', label: 'Last 7 Days' },
                    { v: '14days', label: 'Last 14 Days' },
                    { v: '30days', label: 'Last 30 Days' },
                    { v: 'monthly', label: 'Monthly' },
                  ].map(opt => (
                    <button
                      key={opt.v}
                      onClick={() => {
                        setSelectedSegment(opt.v);
                        setIsSegmentDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm ${
                        selectedSegment === opt.v
                          ? 'text-blue-600 bg-blue-50/50'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          {dataLoading ? (
            <div className="min-h-[400px] flex items-center justify-center">
              <div className="loading-logo-container">
                <img 
                  src="/logoicon.png" 
                  alt="Glowytics Logo" 
                  className="loading-logo"
                />
              </div>
            </div>
          ) : performanceMaxData.campaigns.length === 0 ? (
            <div className="flex justify-center items-center p-12">
              <div className="text-center">
                <p className="text-gray-600 mb-2">No campaign data available</p>
                <p className="text-sm text-gray-500">Connect your Google Ads account to see Performance Max campaign performance</p>
              </div>
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th 
                    className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('text')}
                  >
                    Campaign / Asset Group
                    {sortField === 'text' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('status')}
                  >
                    Status
                    {sortField === 'status' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('budget')}
                  >
                    Cost
                    {sortField === 'budget' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200"
                  >
                    Shopping Cost
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200"
                  >
                    Shopping Revenue
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('impressions')}
                  >
                    Impr.
                    {sortField === 'impressions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('clicks')}
                  >
                    Clicks
                    {sortField === 'clicks' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('ctr')}
                  >
                    CTR
                    {sortField === 'ctr' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('cpc')}
                  >
                    Avg. CPC
                    {sortField === 'cpc' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversions')}
                  >
                    Conv.
                    {sortField === 'conversions' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionRate')}
                  >
                    Conv. Rate
                    {sortField === 'conversionRate' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('costPerConversion')}
                  >
                    Cost/Conv.
                    {sortField === 'costPerConversion' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('conversionValue')}
                  >
                    Revenue
                    {sortField === 'conversionValue' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                  {hasAdjust && (
                    <>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustSessions')}
                      >
                        Adj. Sessions
                        {sortField === 'adjustSessions' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustInstalls')}
                      >
                        Adj. Installs
                        {sortField === 'adjustInstalls' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th 
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustPurchaseEvents')}
                      >
                        Adj. Purchases
                        {sortField === 'adjustPurchaseEvents' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                      <th
                        className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-r border-gray-200 cursor-pointer hover:bg-gray-50"
                        onClick={() => handleSort('adjustAllRevenue')}
                      >
                        Adj. Revenue
                        {sortField === 'adjustAllRevenue' && (
                          <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </th>
                    </>
                  )}
                  <th 
                    className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200 cursor-pointer hover:bg-gray-50"
                    onClick={() => handleSort('roas')}
                  >
                    ROAS
                    {sortField === 'roas' && (
                      <span className="ml-1">{sortDirection === 'asc' ? '↑' : '↓'}</span>
                    )}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {/* Kampanyaları doğrudan performanceMaxData'dan alalım */}
                {sortedCampaigns.map((campaign) => (
                  <React.Fragment key={campaign.id}>
                    {/* Campaign Row */}
                    <tr 
                      className={`hover:bg-blue-50/30 cursor-pointer transition-colors ${
                        expandedCampaigns.has(campaign.id) ? 'bg-blue-50/50' : 'bg-white'
                      }`}
                      onClick={(e) => toggleCampaign(campaign.id, e)}
                    >
                      <td className="py-2.5 px-3 border-r border-gray-200">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center w-5 h-5">
                            {expandedCampaigns.has(campaign.id) ? 
                              <ChevronDown className="w-4 h-4 text-blue-500" /> :
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            }
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-transparent flex items-center justify-center">
                              <img 
                                src="/performancemax.svg" 
                                alt="Performance Max Campaign" 
                                className="w-4 h-4"
                              />
                            </div>
                            <div>
                              <p className="text-[13px] font-medium text-gray-900">{campaign.name}</p>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          campaign.status === 'ENABLED' || campaign.status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'
                        }`}>
                          {campaign.status === 'ENABLED' ? 'Active' : campaign.status || 'Active'}
                        </span>
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200">
                        <div className="text-[13px] font-medium text-gray-900">₺{formatNumber(campaign.spend, { precision: 0 })}</div>
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200">
                        <div className="text-[13px] font-medium text-gray-900">
                          {(() => {
                            const match = campaignShoppingSummary.find(x => (x.campaign_id && x.campaign_id === campaign.id) || (x.campaign_name && x.campaign_name === campaign.name));
                            const val = match ? match.cost || 0 : 0;
                            return `₺${formatNumber(val, { precision: 0 })}`;
                          })()}
                        </div>
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200">
                        <div className="text-[13px] font-medium text-gray-900">
                          {(() => {
                            const match = campaignShoppingSummary.find(x => (x.campaign_id && x.campaign_id === campaign.id) || (x.campaign_name && x.campaign_name === campaign.name));
                            const val = match ? match.revenue || 0 : 0;
                            return `₺${formatNumber(val, { precision: 0 })}`;
                          })()}
                        </div>
                      </td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.impressions || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.clicks || 0).toLocaleString()}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.ctr) ? '0.00' : campaign.ctr.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.cpc) ? '0.00' : campaign.cpc.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{campaign.conversions || 0}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{isNaN(campaign.conversionRate) ? '0.00' : campaign.conversionRate.toFixed(2)}%</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{isNaN(campaign.costPerConversion) ? '0.00' : campaign.costPerConversion.toFixed(2)}</td>
                      <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(campaign.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</td>
                      {hasAdjust && (
                        <>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustSessions || campaign.adjust_sessions || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustInstalls || campaign.adjust_installs || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">{(campaign.adjustPurchaseEvents || campaign.adjust_purchase_events || 0).toLocaleString()}</td>
                          <td className="text-right py-2.5 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-900">₺{(campaign.adjustAllRevenue || campaign.adjust_all_revenue || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                        </>
                      )}
                      <td className="text-right py-2.5 px-3 text-[13px] font-medium text-gray-900">{isNaN(campaign.roas) ? '0.00' : campaign.roas.toFixed(2)}x</td>
                    </tr>

                    {/* Daily / Monthly Breakdown Rows (segment selected) */}
                    {expandedCampaigns.has(campaign.id) && selectedSegment !== 'none' && (
                      <>
                        {(selectedSegment === 'monthly' ? getMonthlyDataForCampaign(campaign.id) : getDailyDataForCampaign(campaign.id)).map((row) => (
                          <tr
                            key={`${campaign.id}_${row.date}`}
                            className={`hover:bg-blue-50/10 ${row.isEmpty ? 'bg-red-50/30 border-l-2 border-red-300' : 'bg-blue-50/5'}`}
                          >
                            <td className="py-2 px-3 border-r border-gray-200">
                              <div className="flex items-center gap-2 pl-[2.75rem]">
                                <Calendar className="w-3 h-3 text-gray-400" />
                                <div>
                                  <p className={`text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                    {selectedSegment === 'monthly'
                                      ? new Date(`${row.date}-01`).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
                                      : new Date(row.date).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' })}
                                  </p>
                                  {row.isEmpty && <p className="text-[11px] text-red-400">No data</p>}
                                </div>
                              </div>
                            </td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `₺${formatNumber(row.spend, { precision: 0 })}`}
                            </td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-400">—</td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : (row.impressions || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : (row.clicks || 0).toLocaleString()}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `${(row.ctr || 0).toFixed(2)}%`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `₺${(row.cpc || 0).toFixed(2)}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : (row.conversions || 0)}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `${(row.conversionRate || 0).toFixed(2)}%`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `₺${(row.costPerConversion || 0).toFixed(2)}`}
                            </td>
                            <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `₺${formatNumber(row.conversionValue || 0, { precision: 0 })}`}
                            </td>
                            {hasAdjust && (
                              <>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {row.isEmpty ? '—' : (row.adjust_sessions || 0).toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {row.isEmpty ? '—' : (row.adjust_installs || 0).toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {row.isEmpty ? '—' : (row.adjust_purchase_events || 0).toLocaleString()}
                                </td>
                                <td className={`text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                                  {row.isEmpty ? '—' : `₺${formatNumber(row.adjust_all_revenue || 0, { precision: 0 })}`}
                                </td>
                              </>
                            )}
                            <td className={`text-right py-2 px-3 text-[13px] font-medium ${row.isEmpty ? 'text-gray-400' : 'text-gray-800'}`}>
                              {row.isEmpty ? '—' : `${(row.roas || 0).toFixed(2)}x`}
                            </td>
                          </tr>
                        ))}
                      </>
                    )}

                    {/* Ad Groups Section */}
                    {expandedCampaigns.has(campaign.id) && selectedSegment === 'none' && campaign.adGroups && (
                      <>
                        {sortAdGroups(campaign.adGroups, sortField, sortDirection).map((adGroup) => (
                          <tr 
                            key={adGroup.id}
                            className="hover:bg-blue-50/20 bg-blue-50/5"
                          >
                            <td className="py-2 px-3 border-r border-gray-200">
                              <div className="flex items-center gap-2 pl-[2.75rem]">
                                <div className="w-5 h-5 flex items-center justify-center">
                                  <img 
                                    src="/enableicon.svg" 
                                    alt="Asset Group" 
                                    className="w-3 h-3" 
                                  />
                                </div>
                                <div>
                                  <p className="text-[13px] font-medium text-gray-800">{adGroup.name}</p>
                                </div>
                              </div>
                            </td>
                            <td className="text-right py-2 px-3 border-r border-gray-200">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${
                                adGroup.status === 'ENABLED' || adGroup.status === 'Active' ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'
                              }`}>
                                {adGroup.status === 'ENABLED' ? 'Active' : adGroup.status}
                              </span>
                            </td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{formatNumber(adGroup.spend, { precision: 0 })}</td>
                            {/* Shopping Cost placeholder for alignment */}
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                            {/* Shopping Revenue placeholder for alignment */}
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.impressions || 0).toLocaleString()}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{(adGroup.clicks || 0).toLocaleString()}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.ctr) ? '0.00' : adGroup.ctr.toFixed(2)}%</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.cpc) ? '0.00' : adGroup.cpc.toFixed(2)}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{adGroup.conversions || 0}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">{isNaN(adGroup.conversionRate) ? '0.00' : adGroup.conversionRate.toFixed(2)}%</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{isNaN(adGroup.costPerConversion) ? '0.00' : adGroup.costPerConversion.toFixed(2)}</td>
                            <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">₺{(adGroup.conversionValue || 0).toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</td>
                            {hasAdjust && (
                              <>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                                <td className="text-right py-2 px-3 border-r border-gray-200 text-[13px] font-medium text-gray-800">—</td>
                              </>
                            )}
                            <td className="text-right py-2 px-3 text-[13px] font-medium text-gray-800">{isNaN(adGroup.roas) ? '0.00' : adGroup.roas.toFixed(2)}x</td>
                          </tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
              {/* Toplam Satırı - Tüm kampanyaların toplamı */}
              <tfoot>
                <tr className="bg-gray-100 border-t-2 border-gray-300">
                  <td className="py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    Total
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200">
                    {/* Status sütunu için toplam yok */}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0), { precision: 0 })}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(performanceMaxData.campaigns.reduce((total, campaign) => {
                      const match = campaignShoppingSummary.find(x => (x.campaign_id && x.campaign_id === campaign.id) || (x.campaign_name && x.campaign_name === campaign.name));
                      return total + (match ? (match.cost || 0) : 0);
                    }, 0), { precision: 0 })}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(performanceMaxData.campaigns.reduce((total, campaign) => {
                      const match = campaignShoppingSummary.find(x => (x.campaign_id && x.campaign_id === campaign.id) || (x.campaign_name && x.campaign_name === campaign.name));
                      return total + (match ? (match.revenue || 0) : 0);
                    }, 0), { precision: 0 })}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalImpressions = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.impressions || 0), 0);
                      const totalClicks = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      return totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(2) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalClicks = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      const totalSpend = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      return totalClicks > 0 ? (totalSpend / totalClicks).toFixed(2) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0).toLocaleString()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalClicks = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.clicks || 0), 0);
                      const totalConversions = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0);
                      return totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(2) : '0.00';
                    })()}%
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{(() => {
                      const totalConversions = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.conversions || 0), 0);
                      const totalSpend = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      return totalConversions > 0 ? (totalSpend / totalConversions).toFixed(2) : '0.00';
                    })()}
                  </td>
                  <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                    ₺{formatNumber(performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.conversionValue || 0), 0), { precision: 0 })}
                  </td>
                  {hasAdjust && (
                    <>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        {performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.adjustSessions || campaign.adjust_sessions || 0), 0).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        {performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.adjustInstalls || campaign.adjust_installs || 0), 0).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        {performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.adjustPurchaseEvents || campaign.adjust_purchase_events || 0), 0).toLocaleString()}
                      </td>
                      <td className="text-right py-3 px-3 border-r border-gray-200 font-medium text-[13px] text-gray-900">
                        ₺{formatNumber(performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.adjustAllRevenue || campaign.adjust_all_revenue || 0), 0), { precision: 0 })}
                      </td>
                    </>
                  )}
                  <td className="text-right py-3 px-3 font-medium text-[13px] text-gray-900">
                    {(() => {
                      const totalSpend = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.spend || 0), 0);
                      const totalConversionValue = performanceMaxData.campaigns.reduce((total, campaign) => total + (campaign.conversionValue || 0), 0);
                      return totalSpend > 0 ? (totalConversionValue / totalSpend).toFixed(2) : '0.00';
                    })()}x
                  </td>
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </Card>
      
      {/* Product Group Performance - Two symmetric tables */}
      <div className="grid grid-cols-12 gap-6">
        <Card className="col-span-12 lg:col-span-6 rounded-2xl overflow-hidden bg-white">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Category Cost (Top 20)</h2>
                <p className="text-sm text-gray-500 mt-1">Aggregated by product type (L1/L2/L3)</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Category</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Impr.</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Clicks</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CTR</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CPC</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Cost</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Conv.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {categorySummary.map((c, idx) => (
                  <tr key={`${c.category}_${idx}`} className="hover:bg-blue-50/20">
                    <td className="py-2 px-3 text-[12px] text-gray-800">{c.category}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{(c.impressions || 0).toLocaleString()}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{(c.clicks || 0).toLocaleString()}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{isNaN(c.ctr) ? '0.00' : c.ctr.toFixed(2)}%</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">₺{isNaN(c.cpc) ? '0.00' : c.cpc.toFixed(2)}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">₺{formatNumber(c.cost || 0, { precision: 0 })}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{(c.conversions || 0).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50">
                  <td className="py-2 px-3 text-[12px] font-semibold text-gray-900">Total</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{categorySummary.reduce((s, x) => s + (x.impressions || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{categorySummary.reduce((s, x) => s + (x.clicks || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{(() => { const imp = categorySummary.reduce((s,x)=>s+(x.impressions||0),0); const clk = categorySummary.reduce((s,x)=>s+(x.clicks||0),0); return imp>0?((clk/imp)*100).toFixed(2):'0.00'; })()}%</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">₺{(() => { const clk = categorySummary.reduce((s,x)=>s+(x.clicks||0),0); const cost = categorySummary.reduce((s,x)=>s+(x.cost||0),0); return clk>0?(cost/clk).toFixed(2):'0.00'; })()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">₺{formatNumber(categorySummary.reduce((s, x) => s + (x.cost || 0), 0))}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{categorySummary.reduce((s, x) => s + (x.conversions || 0), 0).toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>

        <Card className="col-span-12 lg:col-span-6 rounded-2xl overflow-hidden bg-white">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Campaign Shopping Performance</h2>
                <p className="text-sm text-gray-500 mt-1">Aggregated by campaign</p>
              </div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Campaign</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Impr.</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Clicks</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CTR</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">CPC</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Cost</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Conv.</th>
                  <th className="text-right py-2 px-3 text-[11px] font-medium text-gray-600 uppercase tracking-wider border-b border-gray-200">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {campaignShoppingSummary.map((c, idx) => (
                  <tr key={`${c.campaign_id || c.campaign_name}_${idx}`} className="hover:bg-blue-50/20">
                    <td className="py-2 px-3 text-[12px] text-gray-800">{c.campaign_name || c.campaign_id}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{(c.impressions || 0).toLocaleString()}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{(c.clicks || 0).toLocaleString()}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{isNaN(c.ctr) ? '0.00' : c.ctr.toFixed(2)}%</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">₺{isNaN(c.cpc) ? '0.00' : c.cpc.toFixed(2)}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">₺{formatNumber(c.cost || 0)}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">{(c.conversions || 0).toLocaleString()}</td>
                    <td className="text-right py-2 px-3 text-[12px] text-gray-800">₺{formatNumber(c.revenue || 0, { precision: 0 })}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50">
                  <td className="py-2 px-3 text-[12px] font-semibold text-gray-900">Total</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{campaignShoppingSummary.reduce((s, x) => s + (x.impressions || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{campaignShoppingSummary.reduce((s, x) => s + (x.clicks || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{(() => { const imp = campaignShoppingSummary.reduce((s,x)=>s+(x.impressions||0),0); const clk = campaignShoppingSummary.reduce((s,x)=>s+(x.clicks||0),0); return imp>0?((clk/imp)*100).toFixed(2):'0.00'; })()}%</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">₺{(() => { const clk = campaignShoppingSummary.reduce((s,x)=>s+(x.clicks||0),0); const cost = campaignShoppingSummary.reduce((s,x)=>s+(x.cost||0),0); return clk>0?(cost/clk).toFixed(2):'0.00'; })()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">₺{formatNumber(campaignShoppingSummary.reduce((s, x) => s + (x.cost || 0), 0), { precision: 0 })}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">{campaignShoppingSummary.reduce((s, x) => s + (x.conversions || 0), 0).toLocaleString()}</td>
                  <td className="text-right py-2 px-3 text-[12px] text-gray-900 font-semibold">₺{formatNumber(campaignShoppingSummary.reduce((s, x) => s + (x.revenue || 0), 0), { precision: 0 })}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </div>

      {/* Custom date modal removed — <DateRangeFilter /> owns the calendar popover now */}
    </div>
  );
}